import { useMaintenanceCheck, MaintenanceScreen } from '../MaintenanceGate';
import { LeaveRosterEntry, sendNotificationEmail } from '../LeaveRoster';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { getAssessmentByRow, submitDecision } from '../api';
import CreditQueryCard from './CreditQueryCard';
import approvers from '../shared/approvers.json';

const GMAPS_KEY = 'AIzaSyAopjtw19bW62Wo_DK7DxTzYAJ1HBWJhig';

// -- Helpers -------------------------------------------------------------------
function esc(s) { if (s == null) return ' - '; return String(s); }
function fmt(n) { if (n == null) return ' - '; return Number(n).toLocaleString(); }
function fmtDateShort(d) {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return String(d); // not a parseable date - show as-is rather than hide it
  const dd = String(dt.getDate()).padStart(2, '0');
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const yyyy = dt.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
}

// Render business_addresses entry - plain string or {address,type,status} object
function AddrRow({ addr, isFirst }) {
  const isObj = typeof addr === 'object' && addr !== null;
  const text = isObj ? addr.address : addr;
  const label = isObj ? `Branch${addr.status ? ' (' + addr.status + ')' : ''}` : isFirst ? 'Registered' : 'Business';
  const color = isObj ? '#7b1fa2' : '#888';
  return (
    <div style={{ fontSize: 13, padding: '6px 0', borderBottom: '1px solid #f3f3f3', color: '#333' }}>
      <span style={{ color, fontSize: 11, marginRight: 8 }}>{label}</span>{text}
    </div>
  );
}

// -- Styles --------------------------------------------------------------------
const s = {
  page: { minHeight: '100vh', background: '#f5f5f5' },
  nav: { background: '#c8102e', padding: '14px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  navLeft: { display: 'flex', alignItems: 'center', gap: 16 },
  navTitle: { color: '#fff', fontWeight: 700, fontSize: 15 },
  backBtn: { background: 'transparent', border: '1px solid rgba(255,255,255,0.4)', color: '#fff', padding: '5px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 12 },
  navUser: { color: 'rgba(255,255,255,0.8)', fontSize: 13 },
  body: { maxWidth: 860, margin: '0 auto', padding: '24px 20px 80px' },
  card: { background: '#fff', borderRadius: 10, padding: '18px 20px', marginBottom: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' },
  cardTitle: { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: '#888', marginBottom: 14 },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 20px' },
  kv: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '8px 0', borderBottom: '1px solid #f3f3f3', fontSize: 14, gap: 16 },
  kvLbl: { color: '#666', minWidth: 140, flexShrink: 0 },
  kvVal: { fontWeight: 600, textAlign: 'right', flex: 1, wordBreak: 'break-word' },
  disclaimer:{ background: '#fff8e1', border: '1px solid #f0c040', borderRadius: 6, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: '#7a5c00' },
  pills: { display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 14 },
  pill: { textAlign: 'center', borderRadius: 8, padding: '10px 6px' },
  pillVal: { fontSize: 18, fontWeight: 700 },
  pillLbl: { fontSize: 11, color: '#666', marginTop: 2 },
  reco: { background: 'linear-gradient(135deg,#c8102e,#8b0000)', color: '#fff', borderRadius: 10, padding: '16px 20px', marginBottom: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  recoAmt: { fontSize: 28, fontWeight: 800 },
  recoLbl: { fontSize: 12, opacity: 0.8, marginBottom: 4 },
  recoTerm: { fontSize: 15, fontWeight: 600 },
  list: { paddingLeft: 18, fontSize: 14, lineHeight: 1.8 },
  rationale: { fontSize: 14, lineHeight: 1.7, color: '#333' },
  photo: { width: '100%', maxHeight: 500, objectFit: 'contain', borderRadius: 8, border: '1px solid #eee', background: '#f8f8f8' },
  decBar: { position: 'fixed', bottom: 0, left: 0, right: 0, background: '#fff', borderTop: '1px solid #eee', padding: '14px 24px', display: 'flex', gap: 10, justifyContent: 'center', boxShadow: '0 -4px 16px rgba(0,0,0,0.08)', zIndex: 100 },
  btn: { padding: '11px 20px', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 },
  modal: { background: '#fff', borderRadius: 12, padding: 28, width: 440, boxShadow: '0 8px 32px rgba(0,0,0,0.2)' },
  modalTitle:{ fontSize: 16, fontWeight: 700, marginBottom: 16 },
  label: { display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4, marginTop: 12, color: '#444' },
  input: { width: '100%', padding: '9px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14 },
  textarea: { width: '100%', padding: '9px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14, resize: 'vertical', minHeight: 80 },
  modalBtns: { display: 'flex', gap: 8, marginTop: 16 },
};

const RISK_BG = { Low: '#e6f4ea', Moderate: '#fff4e5', High: '#fce8e6' };
const RISK_CLR = { Low: '#1e7e34', Moderate: '#b06000', High: '#c5221f' };
const GRADE_BG = { 'New':'#f1f1f1','Tier 1':'#e6f4ea','Tier 2':'#fff4e5','Tier 3':'#fce8e6','Fail':'#fce8e6' };
const GRADE_CLR = { 'New':'#5f6368','Tier 1':'#1e7e34','Tier 2':'#b06000','Tier 3':'#c5221f','Fail':'#c5221f' };
const CONF_BG = { High:'#e8f0fe', Medium:'#fff4e5', Low:'#f1f1f1' };
const CONF_CLR = { High:'#1967d2', Medium:'#b06000', Low:'#5f6368' };

function KV({ label, value }) {
  return (
    <div style={s.kv}>
      <span style={s.kvLbl}>{label}</span>
      <span style={{ ...s.kvVal, textAlign: 'right', maxWidth: '60%', wordBreak: 'break-word' }}>{value ?? ' - '}</span>
    </div>
  );
}

function Modal({ title, children, onClose }) {
  return (
    <div style={s.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={s.modal}>
        <div style={s.modalTitle}>{title}</div>
        {children}
      </div>
    </div>
  );
}

// -- Google Maps component with Street View + multi-pin ------------------------
function OutletMap({ lat, lng, checkinPins }) {
  const mapRef = useRef(null);
  const gMapRef = useRef(null);
  const svPanoRef = useRef(null);
  const svRef = useRef(null);
  const markersRef = useRef([]);
  const infoWinRef = useRef(null);
  const [mode, setMode] = useState('map'); // 'map' | 'satellite' | 'streetview'
  const [svAvail, setSvAvail] = useState(true);
  const [loaded, setLoaded] = useState(false);

  // Load Google Maps script once
  useEffect(() => {
    if (window.google?.maps) { setLoaded(true); return; }
    if (document.getElementById('gm-script')) return;
    const sc = document.createElement('script');
    sc.id = 'gm-script';
    sc.src = `https://maps.googleapis.com/maps/api/js?key=${GMAPS_KEY}&libraries=geometry`;
    sc.async = true;
    sc.onload = () => setLoaded(true);
    document.head.appendChild(sc);
  }, []);

  const initMap = useCallback(() => {
    if (!mapRef.current || !window.google?.maps) return;
    if (gMapRef.current) return; // never rebuild - would orphan the old map + burn a WebGL context
    const center = { lat: parseFloat(lat), lng: parseFloat(lng) };

    const map = new window.google.maps.Map(mapRef.current, {
      center,
      zoom: 18,
      mapTypeId: 'roadmap',
      streetViewControl: false,
      mapTypeControl: false,
      fullscreenControl: false,
      zoomControl: true,
    });
    gMapRef.current = map;
    svPanoRef.current = null; // street view uses separate div now

    const iw = new window.google.maps.InfoWindow();
    infoWinRef.current = iw;

    // Outlet master marker (red) - permanent, not part of the checkin-pin redraw cycle
    const outletMarker = new window.google.maps.Marker({
      position: center,
      map,
      title: 'Outlet (master record)',
      icon: {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 10,
        fillColor: '#c8102e',
        fillOpacity: 1,
        strokeColor: '#fff',
        strokeWeight: 2,
      },
      zIndex: 10,
    });
    outletMarker.addListener('click', () => {
      iw.setContent(`
        <div style="font-size:13px;padding:4px 2px">
          <strong style="color:#c8102e"> Outlet (Master Record)</strong><br/>
          <span style="color:#666">${lat}, ${lng}</span><br/>
          <span style="color:#b06000;font-size:11px">[!] Salesman-inputted - may be inaccurate</span>
        </div>`);
      iw.open(map, outletMarker);
    });
  }, [lat, lng]);

  // Init map once when script loaded (never rebuilt/hidden after this)
  useEffect(() => {
    if (loaded) initMap();
  }, [loaded, initMap]);

  // Draw/redraw check-in markers whenever the pin list actually changes,
  // without ever touching the map or panorama instance.
  useEffect(() => {
    const map = gMapRef.current;
    const iw = infoWinRef.current;
    if (!map || !iw || !window.google?.maps) return;

    markersRef.current.forEach(m => m.setMap(null));
    markersRef.current = [];

    const center = { lat: parseFloat(lat), lng: parseFloat(lng) };

    checkinPins.forEach((p, i) => {
      const pos = { lat: parseFloat(p.geocode_lat), lng: parseFloat(p.geocode_lng) };
      const marker = new window.google.maps.Marker({
        position: pos,
        map,
        title: `Check-in ${i + 1}`,
        label: {
          text: String(i + 1),
          color: '#fff',
          fontSize: '11px',
          fontWeight: 'bold',
        },
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 12,
          fillColor: '#1967d2',
          fillOpacity: 1,
          strokeColor: '#fff',
          strokeWeight: 2,
        },
        zIndex: 5,
      });

      const contentLines = [
        `<strong style="color:#1967d2">Check-in ${i + 1}</strong>`,
        p.check_in_date ? ` ${p.check_in_date}${p.check_in_time ? ' ' + p.check_in_time : ''}` : null,
        p.photo_type ? ` ${p.photo_type.replace(/_/g, ' ')}` : null,
        p.signboard_text ? ` "${p.signboard_text}"` : null,
        p.note ? ` ${p.note}` : null,
        `<span style="color:#666;font-size:11px">${p.geocode_lat}, ${p.geocode_lng}</span>`,
      ].filter(Boolean).join('<br/>');

      marker.addListener('click', () => {
        iw.setContent(`<div style="font-size:13px;padding:4px 2px;line-height:1.7">${contentLines}</div>`);
        iw.open(map, marker);
      });

      marker.addListener('mouseover', () => {
        iw.setContent(`<div style="font-size:13px;padding:4px 2px;line-height:1.7">${contentLines}</div>`);
        iw.open(map, marker);
      });

      markersRef.current.push(marker);
    });

    if (checkinPins.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      bounds.extend(center);
      checkinPins.forEach(p => bounds.extend({ lat: parseFloat(p.geocode_lat), lng: parseFloat(p.geocode_lng) }));
      map.fitBounds(bounds, { top: 40, bottom: 40, left: 40, right: 40 });
      window.google.maps.event.addListenerOnce(map, 'bounds_changed', () => {
        if (map.getZoom() > 18) map.setZoom(18);
      });
    }
  }, [loaded, lat, lng, checkinPins]);

  // Drive map type / street view visibility purely off `mode`
  useEffect(() => {
    if (!gMapRef.current) return;
    const map = gMapRef.current;

    if (mode === 'map' || mode === 'satellite') {
      map.setMapTypeId(mode === 'satellite' ? 'satellite' : 'roadmap');
      // Destroy existing panorama if any
      if (svPanoRef.current) {
        svPanoRef.current.setVisible(false);
        svPanoRef.current = null;
      }
      return;
    }

    // mode === 'streetview'
    if (!svRef.current) return;
    const center = { lat: parseFloat(lat), lng: parseFloat(lng) };
    const sv = new window.google.maps.StreetViewService();
    let cancelled = false;
    sv.getPanorama({ location: center, radius: 100 }, (data, status) => {
      if (cancelled) return;
      if (status !== 'OK') {
        setSvAvail(false);
        setMode('map');
        return;
      }
      setSvAvail(true);
      const pano = new window.google.maps.StreetViewPanorama(svRef.current, {
        position: center,
        pov: { heading: 0, pitch: 0 },
        zoom: 1,
        addressControl: false,
        fullscreenControl: false,
        motionTracking: false,
        motionTrackingControl: false,
      });
      // Sync back button to React state
      pano.addListener('visible_changed', () => {
        if (!pano.getVisible()) setMode('map');
      });
      svPanoRef.current = pano;
    });
    return () => { cancelled = true; };
  }, [mode, lat, lng]);

  const modeBtn = (key, label) => (
    <button
      key={key}
      onClick={() => setMode(key)}
      style={{
        padding: '5px 14px', borderRadius: 6, fontSize: 11, fontWeight: 600,
        cursor: 'pointer', border: 'none',
        background: mode === key ? '#c8102e' : '#eee',
        color: mode === key ? '#fff' : '#333',
        transition: 'background 0.15s',
      }}
    >{label}</button>
  );

  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>
        Location
      </div>

      {/* Controls */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 8, alignItems: 'center' }}>
        {modeBtn('map', 'Map')}
        {modeBtn('satellite', 'Satellite')}
        {modeBtn('streetview', 'Street View')}
        <a
          href={`https://www.google.com/maps?q=${lat},${lng}&z=18`}
          target="_blank" rel="noopener noreferrer"
          style={{ marginLeft: 'auto', padding: '5px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600, color: '#c8102e', textDecoration: 'none', background: '#fff0f0' }}
        >Open in Maps </a>
      </div>

      {/* Map container */}
      <div
        ref={mapRef}
        style={{ display: mode === 'streetview' ? 'none' : 'block', width: '100%', height: 320, borderRadius: 8, border: '1px solid #eee', background: '#f0f0f0' }}
      />
      {/* Street View container - separate div, shown only in streetview mode */}
      <div
        ref={svRef}
        style={{ display: mode === 'streetview' ? 'block' : 'none', width: '100%', height: 320, borderRadius: 8, border: '1px solid #eee', background: '#f0f0f0' }}
      />

      {!svAvail && (
        <div style={{ fontSize: 11, color: '#b06000', marginTop: 4 }}>
          [!] Street View not available for this location
        </div>
      )}

      {/* Check-in pin legend */}
      {checkinPins.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#555', marginBottom: 6 }}>
             Valid Check-in Geocodes ({checkinPins.length}) - click pins on map for details
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {checkinPins.map((p, i) => (
              <a
                key={i}
                href={`https://www.google.com/maps?q=${p.geocode_lat},${p.geocode_lng}&z=18`}
                target="_blank" rel="noopener noreferrer"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '5px 10px', borderRadius: 6, fontSize: 12,
                  background: '#e8f0fe', color: '#1967d2', textDecoration: 'none', fontWeight: 500,
                  width: 'fit-content',
                }}
              >
                <span style={{
                  background: '#1967d2', color: '#fff', borderRadius: '50%',
                  width: 18, height: 18, display: 'inline-flex', alignItems: 'center',
                  justifyContent: 'center', fontSize: 10, fontWeight: 700, flexShrink: 0,
                }}>{i + 1}</span>
                <span>
                  {p.check_in_date}{p.check_in_time ? ' ' + p.check_in_time : ''}
                  {p.photo_type && <span style={{ color: '#5f6368', fontWeight: 400 }}> . {p.photo_type.replace(/_/g, ' ')}</span>}
                  {p.signboard_text && <span style={{ color: '#1e7e34', fontWeight: 600 }}> . "{p.signboard_text}"</span>}
                </span>
              </a>
            ))}
          </div>
        </div>
      )}

      <div style={{ fontSize: 11, color: '#b06000', marginTop: 6 }}>
        [!] Red pin = master coordinates (salesman-inputted, may be inaccurate)
        {checkinPins.length > 0 && ' . Blue pins = verified check-in geocodes'}
      </div>
    </div>
  );
}

// -- Main component ------------------------------------------------------------
export default function AssessmentPage() {
  const { mcRow } = useParams();
  const { user, logout } = useAuth();
  const maint = useMaintenanceCheck('approver');
  const navigate = useNavigate();
  const location = useLocation();
  const [profileOpen, setProfileOpen] = useState(false);
  const [leaveModalOpen, setLeaveModalOpen] = useState(false);
  const [showAllPhotos, setShowAllPhotos] = useState(false);
  const [showRevenueBreakdown, setShowRevenueBreakdown] = useState(false);
  const [enlargedPhoto, setEnlargedPhoto] = useState(null);
  useEffect(() => {
    if (!profileOpen) return;
    const close = () => setProfileOpen(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [profileOpen]);

  const aiaSheetRowHint = location.state?.aiaSheetRow;
  const [aiaSheetRow, setAiaSheetRow] = useState(aiaSheetRowHint || null);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modal, setModal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expandedHistorical, setExpandedHistorical] = useState(new Set());
  const [toast, setToast] = useState('');

  const [justification, setJustification] = useState('');
  const [customLimit, setCustomLimit] = useState('');
  const [customTerm, setCustomTerm] = useState('');
  const [queryText, setQueryText] = useState('');
  const [recDecision, setRecDecision] = useState('as_recommended');
  const [recLimit, setRecLimit] = useState('');
  const [recTerm, setRecTerm] = useState('');
  const [recJustification, setRecJustification] = useState('');
  const [escalate, setEscalate] = useState(null); // null | { stage:'ask'|'confirm', type, limit, term, justification, target }
  const [tciConfirm, setTciConfirm] = useState(false);
  // Overdue gate - resets naturally on every page load/revisit (plain
  // useState, no persistence), matching the requirement that a fresh
  // check is required every time this page is opened.
  const [overdueChecked, setOverdueChecked] = useState(false);
  const [overdueBlocked, setOverdueBlocked] = useState(false);
  const [overdueCheckLoading, setOverdueCheckLoading] = useState(false);
  const [overduePopup, setOverduePopup] = useState(null); // null | { type: 'clear'|'upcoming'|'overdue', invoices: [] }

  async function handleOverdueCheck() {
    setOverdueCheckLoading(true);
    try {
      const res = await fetch(`/api/assessments/overdue-check/${data.outletId}`);
      const result = await res.json();
      if (result.hasOverdue) {
        setOverdueBlocked(true);
        setOverdueChecked(false);
        setOverduePopup({ type: 'overdue', invoices: result.overdueInvoices });
      } else if (result.hasUpcoming) {
        setOverdueBlocked(false);
        setOverdueChecked(true);
        setOverduePopup({ type: 'upcoming', invoices: result.upcomingInvoices });
      } else {
        setOverdueBlocked(false);
        setOverdueChecked(true);
        setOverduePopup({ type: 'clear', invoices: [] });
      }
    } catch (e) {
      showToast(e.message, true);
    }
    setOverdueCheckLoading(false);
  }
  const [kyc2Modal, setKyc2Modal] = useState(false);
  const [signboardAck2, setSignboardAck2] = useState(false);
  const [kyc2Decision, setKyc2Decision] = useState('');
  const [kyc2Reason, setKyc2Reason] = useState('');
  const [kyc2Remarks, setKyc2Remarks] = useState('');
  const [kyc2Saving, setKyc2Saving] = useState(false);
  // Inline "Confirm Related Party" - separate from the bottom Approve/Reject/
  // KIV decision, per Jun: keep it per-row in the duplicate table, don't mash
  // it into the decision card below.
  const [relationConfirmOpen, setRelationConfirmOpen] = useState(null); // outlet_id currently showing the group-name input
  const [groupNameInput, setGroupNameInput] = useState('');
  const [relationConfirmSaving, setRelationConfirmSaving] = useState(false);

  useEffect(() => {
    getAssessmentByRow(mcRow, aiaSheetRow)
      .then(d => { setData(d); if (d.aiaSheetRow) setAiaSheetRow(d.aiaSheetRow); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
    // Reset all modal state when navigating to a different assessment
    setModal(null);
    setKyc2Modal(false);
    setKyc2Decision('');
    setKyc2Reason('');
    setKyc2Remarks('');
    setJustification('');
    setCustomLimit('');
    setCustomTerm('');
    setQueryText('');
  }, [mcRow]);

  function showToast(msg, isError) {
    setToast(msg);
    setTimeout(() => setToast(''), isError ? 8000 : 3000);
  }

  function handleKyc2Submit() {
    if (!kyc2Decision) { showToast('Select a decision'); return; }
    if (kyc2Decision === 'Reject' && !kyc2Reason) { showToast('Rejection reason required'); return; }
    setKyc2Saving(true);
    fetch(`/api/kyc2/${mcRow}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        status: kyc2Decision,
        rejection_reason: kyc2Reason,
        remarks: kyc2Remarks,
        approverName: user.displayName,
        bizExpiry: data.bizExpiry || '',
        // BRN as confirmed at L2 - this is the value CAS actually verified,
        // not necessarily what Amast currently has on file. Only used by
        // server.js when status === 'Approve'; on Reject the server reads
        // Amast's existing BRN instead (nothing new to confirm).
        business_reg_no: ctx.request?.reg_no || '',
      }),
    })
      .then(r => r.json())
      .then(result => {
        setKyc2Saving(false);
        if (result.success) {
          // One-way heads-up to the requestor when L2 is put on hold (KIV). Sent from this
          // browser, same mechanism as the credit-query emails. No reply path by design.
          if (kyc2Decision === 'KIV' && data.requestorEmail) {
            sendNotificationEmail({
              to: data.requestorEmail,
              subject: `[Ninja Mart Credit] KYC On Hold Ref #${mcRow} (${data.outletId || ''})`,
              text: `Hi,\n\nThe KYC review for Ref #${mcRow} has been put on hold by ${user.displayName}.\n\nReason:\n"${(kyc2Remarks || '').trim()}"\n\nPlease follow up with ${user.displayName} directly. Log in to the Ninja Mart Credit System to view the status.`,
            });
          }
          if (result.amastSync && result.amastSync.success === false && result.amastSync.error !== 'skipped') {
            // KYC decision itself succeeded and is saved - only the Amast
            // sync failed. Don't block navigation, just warn so someone
            // knows to check/retry the Amast side separately.
            showToast(`[OK] KYC ${kyc2Decision}d - but Amast sync failed: ${result.amastSync.error}`, true);
          } else {
            showToast(`[OK] KYC ${kyc2Decision}d`);
          }
          setKyc2Modal(false);
          setTimeout(() => navigate('/'), 800);
        } else if (result.unresolved_count) {
          showToast(`[BLOCKED] ${result.unresolved_count} Related Party flag(s) still need confirmation before Approve`, true);
        } else {
          showToast(result.error || 'Save failed');
        }
      })
      .catch(e => { setKyc2Saving(false); showToast(e.message); });
  }

  function handleConfirmRelation(outletId) {
    if (!groupNameInput.trim()) { showToast('Group Name is required', true); return; }
    setRelationConfirmSaving(true);
    const om = ctx.outlet_master || {};
    fetch(`/api/kyc2/${mcRow}/confirm-relation`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        outlet_id: outletId,
        group_name: groupNameInput.trim(),
        approverName: user.displayName,
        subject_outlet_id: data.outletId,
        subject_outlet_name: om.outlet_name || data.outletName || '',
        subject_address: om.address || '',
        subject_city: om.city || '',
        subject_postcode: om.postcode || '',
        subject_state: om.state || '',
        subject_regno: om.reg_no || '',
      }),
    })
      .then(r => r.json())
      .then(async result => {
        setRelationConfirmSaving(false);
        if (result.success) {
          showToast('[OK] Related Party confirmed');
          setRelationConfirmOpen(null);
          setGroupNameInput('');
          // Refetch so the row's relation_confirmed_by_l2 state (and the
          // Approve block check) reflect the update without a full navigation.
          try {
            const fresh = await getAssessmentByRow(mcRow, aiaSheetRow);
            setData(fresh);
          } catch (e) { /* non-fatal - user can still see it after a manual refresh */ }
        } else {
          showToast(result.error || 'Confirm failed', true);
        }
      })
      .catch(e => { setRelationConfirmSaving(false); showToast(e.message, true); });
  }

  async function handleAction(action) {
    if (!data) return;
    setSaving(true);
    try {
      const result = await submitDecision(mcRow, action, {
        justification,
        customLimit,
        customTerm,
        queryText,
        approverEmail: user.email,
        approverName: user.displayName,
      });
      if (result.success) {
        if (result.amastSync && result.amastSync.success === false && result.amastSync.error !== 'skipped') {
          showToast(`[OK] ${result.decision} recorded - but Amast sync failed: ${result.amastSync.error}`, true);
        } else {
          showToast(`[OK] ${result.decision} recorded`, false);
        }
        setModal(null);
        setTimeout(() => navigate('/'), 1500);
      } else {
        showToast(result.error || 'Unknown error', true);
      }
    } catch(e) {
      showToast(e.message, true);
    }
    setSaving(false);
  }

  function resolveEscalationTarget(limit) {
    const entries = Object.entries(approvers)
      .filter(([email, r]) => r.role === 'approver' && email !== user.email)
      .sort((a, b) => a[1].level - b[1].level);
    const covering = entries.find(([, r]) => r.maxLimit >= limit);
    if (covering) return { email: covering[0], name: covering[1].name, ceiling: false };
    const highest = entries.reduce((max, e) => (e[1].level > max[1].level ? e : max), entries[0]);
    return { email: highest[0], name: highest[1].name, ceiling: true };
  }

  async function handleEscalate() {
    if (!escalate) return;
    setSaving(true);
    try {
      const res = await fetch(`/api/assessments/${mcRow}/recommend`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          recommended_decision: escalate.type,
          recommended_limit: escalate.limit,
          recommended_term: escalate.term,
          justification: escalate.justification,
          approverEmail: user.email,
          approverName: user.displayName,
          aiaRow: data.aiaSheetRow,
        }),
      });
      const result = await res.json();
      if (result.success) { if (result.notify) sendNotificationEmail(result.notify); showToast(`[OK] Escalated to ${escalate.target.name}`, false); setModal(null); setEscalate(null); setTimeout(() => navigate('/'), 1500); }
      else showToast(result.error || 'Failed', true);
    } catch(e) { showToast(e.message, true); }
    setSaving(false);
  }

  // Memoized here (before the early returns below) so hook order stays
  // stable across loading/error/data renders. Optional-chained since `data`
  // can still be null at this point. Without this being stable, OutletMap's
  // initMap identity churns on every render, rebuilding the Google Map (and
  // its linked Street View panorama) repeatedly and burning through the
  // browser's WebGL context limit until Street View renders black.
  const checkinPins = useMemo(() => {
    const vpa = data?.visitPhotoAssessment || data?.context?.visit_photo_assessment || {};
    // Parse Visit Photo now outputs lat/lng (from van_visits) and check_in_at
    // (ISO timestamp) - OutletMap internally still reads geocode_lat/
    // geocode_lng/check_in_date/check_in_time, so alias here rather than
    // touching every usage inside OutletMap.
    // Sorted explicitly by check_in_at (chronological) so pin numbering is
    // deterministic and matches the gallery's numbering below - was
    // previously just whatever order Gemini's response happened to list
    // photos in, which isn't guaranteed stable or meaningful.
    return (vpa.photos || [])
      .filter(p => p.lat && p.lng && p.checkin_valid)
      .sort((a, b) => new Date(a.check_in_at || a.date || 0) - new Date(b.check_in_at || b.date || 0))
      .map(p => {
        const dt = p.check_in_at ? new Date(p.check_in_at) : null;
        return {
          ...p,
          geocode_lat: p.lat,
          geocode_lng: p.lng,
          check_in_date: p.date || (dt ? dt.toISOString().slice(0, 10) : null),
          check_in_time: dt ? dt.toTimeString().slice(0, 5) : null,
        };
      });
  }, [data]);

  // Same merge logic the gallery below needs, lifted to component level so
  // the lightbox (rendered in a separate scope, outside the gallery's IIFE)
  // can also look up a specific photo by row_index - needed for the
  // "Compare with matched photo" button on a flagged re-photograph.
  const mergedPhotos = useMemo(() => {
    const vpaLocal = data?.visitPhotoAssessment || data?.context?.visit_photo_assessment || {};
    const jasperPhotosLocal = data?.jasperPhotoFileIds || data?.context?.jasper_photo_file_ids || [];
    const checkinNumberByRowIndex = new Map(
      checkinPins.map((p, i) => [p.row_index, i + 1])
    );
    return (vpaLocal.photos || []).map(p => {
      const match = jasperPhotosLocal.find(jp => jp.date === p.date && jp.van_id === p.van_id);
      return { ...p, file_id: match?.file_id || null, checkin_number: checkinNumberByRowIndex.get(p.row_index) || null };
    }).sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
  }, [data, checkinPins]);

  if (loading) return <div style={{ padding: 48, textAlign: 'center', color: '#888' }}>Loading assessment...</div>;
  if (error) return <div style={{ padding: 48, textAlign: 'center', color: '#c5221f' }}>Error: {error}</div>;
  if (!data) return <div style={{ padding: 48, textAlign: 'center', color: '#888' }}>Assessment not found.</div>;

  const a = data.assessment || {};
  const ctx = data.context || {};
  const ctos = ctx.ctos || {};
  const rm = ctx.repayment_metrics || {};
  const os = ctx.online_search || {};
  const om = ctx.outlet_master || {};
  const vpa = data.visitPhotoAssessment || ctx.visit_photo_assessment || {};
  const mi = ctx.market_intelligence || {};
  const invoices = ctx.invoices || [];

  // Signboard vs registered/trading name - same deterministic check as
  // KYCPage.js's L1 stage, mirrored here so L2 sign-off also gets an
  // explicit flag instead of relying on the AI narrative to catch it.
  function normalizeName(s) {
    return String(s || '')
      .toUpperCase()
      .replace(/\b(SDN\.?\s*BHD\.?|BERHAD|ENTERPRISE|TRADING|SUPPLIES?)\b/g, '')
      .replace(/[^A-Z0-9]/g, '')
      .trim();
  }
  const signboardTexts = [...new Set((vpa.photos || []).map(p => p.signboard_text).filter(Boolean))];
  const namesToCheck = [om.outlet_name, ctos.entity_name].filter(Boolean).map(normalizeName);
  const signboardHasEvidence = signboardTexts.length > 0 && namesToCheck.length > 0;
  const signboardMatches = signboardHasEvidence && signboardTexts.some(txt => {
    const norm = normalizeName(txt);
    return namesToCheck.some(n => n && norm && (norm.includes(n) || n.includes(norm)));
  });
  // Small-print ownership/licensee text (e.g. "Dimiliki oleh") - often the
  // MORE reliable match for the registered name than the main brand
  // signboard, especially for franchise/petrol-mart outlets where the
  // storefront brand (Mesra, Shell Select) legitimately differs from the
  // registered operating entity. A match here clears a signboard mismatch
  // rather than being ignored - but ownership_text is frequently not
  // captured at all (salesman photo coverage), so this only helps when
  // present; absence doesn't clear anything on its own.
  const ownershipTexts = [...new Set((vpa.photos || []).map(p => p.ownership_text).filter(Boolean))];
  const ownershipHasEvidence = ownershipTexts.length > 0 && namesToCheck.length > 0;
  const ownershipMatches = ownershipHasEvidence && ownershipTexts.some(txt => {
    const norm = normalizeName(txt);
    return namesToCheck.some(n => n && norm && (norm.includes(n) || n.includes(norm)));
  });
  const signboardMismatch = signboardHasEvidence && !signboardMatches && !ownershipMatches;

  const kyc2 = data.kyc2 || {};
  const kyc2Done = kyc2.status === 'Approve' || kyc2.status === 'Reject';
  const kyc2Approved = kyc2.status === 'Approve';
  // kyc2Done intentionally excludes KIV elsewhere (decision buttons should
  // stay active for a KIV'd case, since it isn't a final outcome) - this
  // separate flag is just for whether there's decision info worth showing.
  const kyc2HasInfo = kyc2Done || kyc2.status === 'KIV';
  // If kyc2 is rejected but AIA col L not yet updated, treat as Fail KYC
  const effectiveDecision = (kyc2.status === 'Reject' && (!data.finalDecision || data.finalDecision === 'Pending'))
    ? 'Fail KYC'
    : data.finalDecision;
  const limitDecided = effectiveDecision && !['Pending','Pending KYC Check','Pending KYC Approval','Pending Limit Approval','Waiting for Sales Reply','Credit Query'].includes(effectiveDecision);
  const decided = limitDecided;

  // Stage color for page theming
  const stageColor = limitDecided
    ? (effectiveDecision?.startsWith('Approved') ? '#1e7e34' : '#c5221f')
    : kyc2Approved ? '#1967d2'
    : effectiveDecision === 'Fail KYC' ? '#c5221f'
    : '#b06000'; // amber = pending KYC approval

  if (maint.blocked) return <MaintenanceScreen />;
  return (
    <div style={s.page}>

      {/* Nav */}
      <nav style={s.nav}>
        <div style={s.navLeft}>
          <button style={s.backBtn} onClick={() => navigate('/')}>← Back to Credit Assessment</button>
          <img
            src="/logo-wordmark.svg"
            alt="Ninja Mart"
            width={28 * 3.84}
            height={28}
            style={{ height: 28, width: 28 * 3.84, objectFit: 'contain', marginRight: 8 }}
            onError={e => { e.target.style.display = 'none'; }}
          />
          <div style={s.navTitle}>{data.outletId} - {data.outletName}</div>
        </div>
        <div style={{ position: 'relative' }}>
          <div
            style={{ color: 'rgba(255,255,255,0.9)', fontSize: 13, cursor: 'pointer', userSelect: 'none', textAlign: 'right' }}
            onClick={e => { e.stopPropagation(); setProfileOpen(p => !p); }}
          >
            <div style={{ fontWeight: 600, color: '#fff' }}>{user.displayName} ▾</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', marginTop: 1 }}>
              L{user.level} Approver . Max RM {fmt(user.maxLimit)}
            </div>
          </div>
          {profileOpen && (
            <div style={{
              position: 'absolute', top: '100%', right: 0, marginTop: 8,
              background: '#fff', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
              minWidth: 160, zIndex: 100, overflow: 'hidden',
            }}>
              <button
                style={{ width: '100%', padding: '12px 16px', background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', fontSize: 13, color: '#333' }}
                onClick={e => { e.stopPropagation(); setProfileOpen(false); setLeaveModalOpen(true); }}
              >Set Leave Roster</button>
              <button
                style={{ width: '100%', padding: '12px 16px', background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', fontSize: 13, color: '#c5221f', fontWeight: 600 }}
                onClick={e => { e.stopPropagation(); logout(); }}
              >Sign out</button>
            </div>
          )}
          <LeaveRosterEntry open={leaveModalOpen} onClose={() => setLeaveModalOpen(false)} />
        </div>
      </nav>

      {/* Stage color bar */}
      <div style={{ height: 4, background: stageColor }} />
      <div style={{ ...s.body, '--stage-color': stageColor }}>

        {/* Status banner */}
        {limitDecided ? (
          <div style={{ background: data.finalDecision?.startsWith('Approved') ? '#e6f4ea' : '#fce8e6', border: `1px solid ${data.finalDecision?.startsWith('Approved') ? '#34a853' : '#c5221f'}`, borderRadius: 8, padding: '10px 16px', marginBottom: 16, color: data.finalDecision?.startsWith('Approved') ? '#1e7e34' : '#c5221f', fontWeight: 600 }}>
            Credit Decision: {data.finalDecision} - MC Row #{mcRow}
          </div>
        ) : data.finalDecision === 'Fail KYC' ? (
          <div style={{ background: '#fce8e6', border: '1px solid #c5221f', borderRadius: 8, padding: '10px 16px', marginBottom: 16, color: '#c5221f', fontWeight: 600 }}>
            Fail KYC - rejected by {kyc2.approver} on {kyc2.date} - MC Row #{mcRow}
          </div>
        ) : kyc2Approved ? (
          <div style={{ background: '#e8f0fe', border: '1px solid #1967d2', borderRadius: 8, padding: '10px 16px', marginBottom: 16, color: '#1967d2', fontWeight: 600 }}>
            KYC Approved - Pending Limit Approval - MC Row #{mcRow}
          </div>
        ) : kyc2Done ? (
          <div style={{ background: '#fce8e6', border: '1px solid #c5221f', borderRadius: 8, padding: '10px 16px', marginBottom: 16, color: '#c5221f', fontWeight: 600 }}>
            KYC Rejected by {kyc2.approver} - {kyc2.rejection_reason} - MC Row #{mcRow}
          </div>
        ) : (
          <div style={{ background: '#fff4e5', border: '1px solid #f0a500', borderRadius: 8, padding: '10px 16px', marginBottom: 16, color: '#b06000', fontWeight: 600 }}>
            [PENDING] Pending KYC Approval - MC Row #{mcRow}
          </div>
        )}

        {/* -- Credit Request -- */}
        <div style={s.card}>
          <div style={s.cardTitle}>Credit Request</div>
          <div style={s.grid2}>
            <KV label="Outlet ID" value={data.outletId} />
            <KV label="Amast Name" value={data.outletName} />
            <KV label="Registered Name (SSM)" value={data.entityName} />
            {ctx.request?.reg_no && <KV label="SSM / Reg No." value={String(ctx.request.reg_no)} />}
            <KV label="Requested Limit" value={'RM ' + fmt(data.reqLimit)} />
            {data.requestorName && <KV label="Submitted By" value={[data.requestorName, data.region].filter(Boolean).join(' . ')} />}
          </div>
          {data.salesRemarks && (
            (() => {
                let rm = {};
                try { rm = JSON.parse(data.salesRemarks); } catch(e) { rm = { other_remarks: data.salesRemarks }; }
                const fields = [
                  rm.limitJustification && { label: 'Limit Justification', value: rm.limitJustification },
                  rm.limit_justification && { label: 'Limit Justification', value: rm.limit_justification },
                  rm.latePaymentResponse && { label: 'Late Payment Response', value: rm.latePaymentResponse },
                  rm.late_payment_response && { label: 'Late Payment Response', value: rm.late_payment_response },
                  rm.other_remarks && { label: 'Sales Remarks', value: rm.other_remarks },
                  (rm.is_resubmission && rm.resubmission_reason) && { label: 'Resubmission Reason', value: rm.resubmission_reason },
                  data.monthlyPurchase && { label: 'Expected Monthly Purchase', value: 'RM ' + fmt(data.monthlyPurchase) },
                  data.skuCount && { label: 'Expected SKU Count', value: String(data.skuCount) },
                  data.brandsOfInterest && { label: 'Brands of Interest', value: data.brandsOfInterest },
                ].filter(Boolean);
                if (fields.length === 0) return null;
                return (
                  <div style={{ marginTop: 12 }}>
                    {fields.map(f => (
                      <div key={f.label} style={{ marginBottom: 8, padding: '8px 12px', background: '#f8f9fa', borderRadius: 6 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#c8102e', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>{f.label}</div>
                        <div style={{ fontSize: 13, color: '#555', fontStyle: 'italic' }}>"{f.value}"</div>
                      </div>
                    ))}
                  </div>
                );
              })()
          )}
        </div>

        {/* -- Approver Comment (this case's own decision, not historical) -- */}
        {data.finalDecision && data.finalDecision !== 'Pending' && data.decisionJustification && (
          <div style={{ marginTop: 12, padding: '8px 12px', background: '#f0f4ff', borderRadius: 6, border: '1px solid #d0dcff' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#1967d2', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>
              Approver Comment
              {(data.decisionMaker || data.decisionDate) && (
                <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: '#888', marginLeft: 6 }}>
                  - {data.decisionMaker}{data.decisionMaker && data.decisionDate ? ' on ' : ''}{data.decisionDate}
                </span>
              )}
            </div>
            <div style={{ fontSize: 13, color: '#333' }}>{data.decisionJustification}</div>
          </div>
        )}

        {/* SSM portal-down bypass - automation could not verify this outlet's
            registration number against SSM at submission time. The salesman
            self-certified instead; CTOS's own cached database lookup (and
            any auto-extraction relying on it) cannot be trusted to disambiguate
            a genuinely-new entity from a wrong/mistyped reg no in this case - 
            requires manual CTOS extraction and manual reg-no verification. */}
        {(() => {
          if (!data.salesRemarks) return null;
          let rm = {};
          try { rm = JSON.parse(data.salesRemarks); } catch(e) { return null; }
          if (rm.portal_verified === false && rm.portal_skip_reason === 'PORTAL_DOWN') {
            return (
              <div style={{ background: '#fff4e5', border: '1px solid #ffb84d', borderRadius: 8, padding: '12px 14px', marginBottom: 16 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: '#8a5200', marginBottom: 4 }}>[!] Manual review required - SSM registration not auto-verified</div>
                <div style={{ fontSize: 12, color: '#6b3f00', lineHeight: 1.5 }}>
                  The SSM portal was unavailable at submission time. The registration number was self-certified by the submitting salesperson and was NOT independently verified against SSM's live registry.
                  CTOS's own database lookup cannot reliably tell "genuinely new entity" apart from "wrong registration number" in this case - pull the CTOS report manually and verify the registration number against SSM before proceeding.
                </div>
              </div>
            );
          }
          return null;
        })()}

        {/* -- Submission Attachments (sales upload folder) -- */}
        {data.driveUrl && (
          <div style={s.card}>
            <div style={s.cardTitle}>Submission Attachments</div>
            <a href={data.driveUrl.split(',')[0].trim()} target="_blank" rel="noreferrer"
              style={{ fontSize: 13, color: '#1967d2', fontWeight: 600 }}>
              Open sales submission folder (photos / documents)
            </a>
          </div>
        )}

        {/* -- Outlet Master Info -- */}
        {(om.outlet_name || data.outletPhotoFileId || om.lat) && (
          <div style={s.card}>
            <div style={s.cardTitle}>Outlet Master Info</div>
            <div style={s.grid2}>
              <KV label="Trading Name" value={om.outlet_name} />
              <KV label="Channel" value={om.channel} />
              <KV label="Current Limit" value={'RM ' + fmt(om.current_limit)} />
              <KV label="Current Term" value={om.current_term_days ? om.current_term_days + ' days' : ' - '} />
              <KV label="Visit Cycle" value={om.visit_cycle_days ? om.visit_cycle_days + ' days' : ' - '} />
              <KV label="Van ID" value={om.van_id} />
            </div>

            {om.address && (
              <div style={{ marginTop: 10, padding: '8px 0', borderTop: '1px solid #f3f3f3', fontSize: 13 }}>
                <span style={{ color: '#666', fontSize: 12 }}>Address</span>
                <div style={{ fontWeight: 600, marginTop: 2 }}>{om.address}</div>
              </div>
            )}

            {/* Outlet master photo(s) — up to 3, in a simple row. Falls
                back to the old singular field for rows predating this
                change, so nothing breaks for existing assessments. */}
            {(() => {
              const outletPhotos = (data.outletPhotoFileIds && data.outletPhotoFileIds.length > 0)
                ? data.outletPhotoFileIds
                : (data.outletPhotoFileId ? [data.outletPhotoFileId] : []);
              if (outletPhotos.length === 0) return null;
              return (
                <div style={{ marginTop: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Outlet Photo (Master Record)</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {outletPhotos.map((fileId, i) => (
                      <img
                        key={fileId || i}
                        src={`/api/drive-image/${fileId}`}
                        alt={`outlet ${i + 1}`}
                        style={{ ...s.photo, width: 'auto', flex: 1, minWidth: 0 }}
                        onError={e => e.target.style.display = 'none'}
                      />
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Google Maps + Street View */}
            {om.lat && om.lng && (
              <OutletMap
                lat={om.lat}
                lng={om.lng}
                checkinPins={checkinPins}
              />
            )}
          </div>
        )}

        {/* -- Visit Photos (recent N + Show more) -- */}
        {(() => {
          const jasperPhotos = data.jasperPhotoFileIds || ctx.jasper_photo_file_ids || [];
          if (jasperPhotos.length === 0 && !vpa.flag) return null;

          // Reuse the component-level computation (also used by the
          // lightbox) rather than recomputing it here.
          const merged = mergedPhotos;

          const VISIBLE_COUNT = 6;
          const visible = showAllPhotos ? merged : merged.slice(0, VISIBLE_COUNT);

          return (
            <div style={s.card}>
              <div style={s.cardTitle}>Visit Photos</div>
              {vpa.reference_photo_source === 'google_places_verified' && (
                <div style={{ padding: '8px 12px', background: '#fff8e1', borderRadius: 6, marginBottom: 10, fontSize: 12, color: '#8a6d00' }}>
                  [!] No outlet photo was on file - the reference photo used for comparison below was sourced from Google Maps and AI-verified to show this business. Capture date is unknown (not provided by Google for standard contributed photos).
                </div>
              )}
              {vpa.flag && (
                <div style={{ padding: 12, background: '#f8f9fa', borderRadius: 8, borderLeft: '4px solid ' + (vpa.flag === 'GOOD_EVIDENCE' ? '#1e7e34' : '#c5221f'), marginBottom: 14 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: vpa.flag === 'GOOD_EVIDENCE' ? '#1e7e34' : '#c5221f' }}>{vpa.flag}</div>
                  <div style={{ fontSize: 13, color: '#555', marginTop: 4 }}>{vpa.summary}</div>
                  <div style={{ fontSize: 12, color: '#888', marginTop: 6 }}>
                    {vpa.valid_checkins != null && ('Valid check-ins: ' + vpa.valid_checkins + ' / ' + vpa.total_checkins)}
                    {vpa.last_legit_checkin_date && (' | Last legit: ' + vpa.last_legit_checkin_date)}
                  </div>
                </div>
              )}
              {merged.length === 0 ? (
                <div style={{ fontSize: 13, color: '#888' }}>No visit photos available.</div>
              ) : (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
                    {visible.map((p, i) => (
                      <div key={i} style={{ border: '1px solid #eee', borderRadius: 8, overflow: 'hidden', background: p.checkin_valid ? '#fff' : '#fff8f8', position: 'relative' }}>
                        {p.checkin_number && (
                          <div style={{
                            position: 'absolute', top: 6, left: 6, zIndex: 1,
                            width: 22, height: 22, borderRadius: '50%', background: '#1967d2', color: '#fff',
                            fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
                          }} title={`Matches Check-in ${p.checkin_number} on the map`}>
                            {p.checkin_number}
                          </div>
                        )}
                        {p.file_id ? (
                          <img src={`/api/drive-image/${p.file_id}`} alt={p.photo_type || 'visit photo'}
                            style={{ width: '100%', height: 130, objectFit: 'cover', display: 'block', cursor: 'zoom-in' }}
                            onClick={() => setEnlargedPhoto(p)}
                            onError={e => e.target.style.display = 'none'} />
                        ) : (
                          <div style={{ width: '100%', height: 130, background: '#f3f3f3', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#aaa' }}>No image</div>
                        )}
                        <div style={{ padding: 8 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: p.checkin_valid ? '#1e7e34' : '#c5221f' }}>
                            {p.checkin_valid ? 'Valid check-in' : 'Not a valid check-in'}
                          </div>
                          {!p.checkin_valid && (
                            <div style={{ fontSize: 10, color: '#c5221f', marginTop: 1 }}>
                              {[
                                !p.is_valid_evidence && 'content',
                                p.geocode_too_far && 'too far',
                                p.geocode_implausible && 'geocode implausible',
                                p.flash_visit && 'flash visit',
                                p.possible_rephotograph && 'possible re-photograph',
                              ].filter(Boolean).join(' . ')}
                            </div>
                          )}
                          {(p.photo_type || p.note) && (
                            <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                              {p.photo_type ? p.photo_type.replace(/_/g, ' ') : ''}
                              {p.photo_type && p.note ? ' - ' : ''}
                              {p.note || ''}
                            </div>
                          )}
                          <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{p.date} {p.van_id ? '. ' + p.van_id : ''}</div>
                          {p.lat && p.lng && (
                            <div style={{ fontSize: 10, color: (p.geocode_implausible || p.geocode_too_far) ? '#c5221f' : '#aaa', marginTop: 2 }}>
                              {p.lat}, {p.lng}
                              {p.geocode_implausible ? ` [!] ${p.geocode_distance_km}km from outlet` : ''}
                              {!p.geocode_implausible && p.geocode_too_far ? ` [!] ${p.geocode_distance_m}m from outlet` : ''}
                            </div>
                          )}
                          {p.flash_visit && (
                            <div style={{ fontSize: 10, color: '#c5221f', marginTop: 2 }}>
                              [!] Flash visit - {p.visit_duration_seconds}s check-in to check-out
                            </div>
                          )}
                          {p.possible_rephotograph && (
                            <div style={{ fontSize: 10, color: '#c5221f', marginTop: 2 }}>
                              [!] Possible re-photograph{p.rephotograph_indicators ? `: ${p.rephotograph_indicators}` : ''}
                              {p.matched_photo_row_index != null && (() => {
                                const matched = merged.find(m => m.row_index === p.matched_photo_row_index);
                                return matched ? (
                                  <div>
                                    <button onClick={() => setEnlargedPhoto(matched)}
                                      style={{ marginTop: 3, padding: '2px 8px', fontSize: 10, fontWeight: 600, color: '#1967d2', background: '#e8f0fe', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
                                      {'-> View matched photo ('}{matched.date}{')'}
                                    </button>
                                  </div>
                                ) : null;
                              })()}
                            </div>
                          )}
                          {p.signboard_text && <div style={{ fontSize: 11, color: '#555', marginTop: 4 }}> {p.signboard_text}</div>}
                          {p.ownership_text && <div style={{ fontSize: 11, color: '#1e7e34', marginTop: 2 }}> Ownership text: {p.ownership_text}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                  {merged.length > VISIBLE_COUNT && (
                    <button onClick={() => setShowAllPhotos(v => !v)}
                      style={{ marginTop: 12, padding: '6px 14px', borderRadius: 6, border: '1px solid #ddd', background: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#555' }}>
                      {showAllPhotos ? '^ Show less' : `v Show more (${merged.length - VISIBLE_COUNT} more)`}
                    </button>
                  )}
                </>
              )}
            </div>
          );
        })()}

        {/* Photo lightbox - click any visit photo above to enlarge to its
            highest available resolution. Click outside the image or the x
            to close. Uses object-fit: contain (not cover) so the full image
            is visible, unlike the cropped thumbnail grid above. */}
        {enlargedPhoto && (
          <div
            onClick={() => setEnlargedPhoto(null)}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 1000, cursor: 'zoom-out', padding: 24,
            }}
          >
            <img
              src={`/api/drive-image/${enlargedPhoto.file_id}`}
              alt={enlargedPhoto.photo_type || 'visit photo'}
              onClick={e => e.stopPropagation()}
              style={{ maxWidth: '95vw', maxHeight: '90vh', objectFit: 'contain', cursor: 'default', borderRadius: 4 }}
            />
            <button
              onClick={() => setEnlargedPhoto(null)}
              style={{
                position: 'absolute', top: 20, right: 24, background: 'rgba(255,255,255,0.15)',
                border: 'none', color: '#fff', fontSize: 22, width: 40, height: 40, borderRadius: '50%',
                cursor: 'pointer', lineHeight: '40px', textAlign: 'center',
              }}
            >x</button>
            {(enlargedPhoto.photo_type || enlargedPhoto.note) && (
              <div
                onClick={e => e.stopPropagation()}
                style={{
                  position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)',
                  background: 'rgba(0,0,0,0.6)', color: '#fff', padding: '8px 16px', borderRadius: 6,
                  fontSize: 12, maxWidth: '80vw', textAlign: 'center', cursor: 'default',
                }}
              >
                <div style={{ fontWeight: 700, color: enlargedPhoto.checkin_valid ? '#7ee2a8' : '#ff9999' }}>
                  {enlargedPhoto.checkin_valid ? 'Valid check-in' : 'Not a valid check-in'}
                </div>
                {(enlargedPhoto.photo_type || enlargedPhoto.note) && (
                  <div style={{ marginTop: 2, opacity: 0.85 }}>
                    {enlargedPhoto.photo_type ? enlargedPhoto.photo_type.replace(/_/g, ' ') : ''}
                    {enlargedPhoto.photo_type && enlargedPhoto.note ? ' - ' : ''}
                    {enlargedPhoto.note || ''}
                  </div>
                )}
                {enlargedPhoto.possible_rephotograph && enlargedPhoto.matched_photo_row_index != null && (() => {
                  const matched = mergedPhotos.find(m => m.row_index === enlargedPhoto.matched_photo_row_index);
                  return matched ? (
                    <button onClick={(e) => { e.stopPropagation(); setEnlargedPhoto(matched); }}
                      style={{ marginTop: 6, padding: '4px 10px', fontSize: 11, fontWeight: 600, color: '#1967d2', background: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
                      {'-> Compare with matched photo ('}{matched.date}{')'}
                    </button>
                  ) : null;
                })()}
              </div>
            )}
          </div>
        )}

        {/* -- CTOS Entity -- */}
        {(ctos.entity_name || ctos.entity_status) && (
          <div style={s.card}>
            <div style={s.cardTitle}>CTOS Entity</div>
            <div style={s.grid2}>
              <KV label="Entity Name" value={ctos.entity_name} />
              <KV label="Type" value={ctos.entity_type} />
              {(() => {
                const statusOk = ['EXISTING','ACTIVE','LIVE'].some(s => (ctos.entity_status || '').toUpperCase().includes(s));
                return (
                  <div style={s.kv}>
                    <span style={s.kvLbl}>Status</span>
                    <span style={{ ...s.kvVal, color: statusOk ? '#1e7e34' : '#c5221f', fontWeight: 700 }}>{ctos.entity_status || ' - '}</span>
                  </div>
                );
              })()}
              <KV label="Report Type" value={ctos.report_type} />
              <KV label="Reg Date" value={ctos.registration_date} />
              <KV label="Business Activity" value={ctos.business_activity || ctos.msic || ' - '} />
              {(() => {
                let expiryColor = 'inherit';
                if (ctos.expiry_date) {
                  const parts = ctos.expiry_date.match(/(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})/);
                  if (parts) {
                    const exp = new Date(parts[3], parts[2]-1, parts[1]);
                    if (exp < new Date()) expiryColor = '#c5221f';
                  }
                }
                return (
                  <div style={s.kv}>
                    <span style={s.kvLbl}>Expiry</span>
                    <span style={{ ...s.kvVal, color: expiryColor, fontWeight: expiryColor !== 'inherit' ? 700 : 'inherit' }}>{ctos.expiry_date || ' - '}</span>
                  </div>
                );
              })()}
            </div>
            {ctos.directors?.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#c8102e', textTransform: 'uppercase', letterSpacing: 1, margin: '10px 0 6px', paddingBottom: 4, borderBottom: '2px solid #f3f3f3' }}>Directors</div>
                {ctos.directors.map((d, i) => {
                  const isObj = typeof d === 'object' && d !== null;
                  const name = isObj ? d.name : d;
                  const meta = isObj ? [d.id, d.designation, d.appointed ? 'Appointed ' + d.appointed : null].filter(Boolean).join(' . ') : null;
                  return (
                    <div key={i} style={s.kv}>
                      <span style={s.kvLbl}>{name}</span>
                      {meta && <span style={{ ...s.kvVal, color: '#666', fontWeight: 400 }}>{meta}</span>}
                    </div>
                  );
                })}
              </div>
            )}
            {ctos.owners?.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#c8102e', textTransform: 'uppercase', letterSpacing: 1, margin: '10px 0 6px', paddingBottom: 4, borderBottom: '2px solid #f3f3f3' }}>Owners / Shareholders</div>
                {ctos.owners.map((o, i) => (
                  <KV key={i} label={o.name}
                    value={[
                      o.position,
                      o.shareholding_pct != null ? o.shareholding_pct + '%' : null,
                      o.shares != null ? 'RM ' + fmt(o.shares) : null,
                      o.id ? '(' + o.id + ')' : null,
                    ].filter(Boolean).join(' . ')} />
                ))}
              </div>
            )}
            {ctos.business_addresses?.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#c8102e', textTransform: 'uppercase', letterSpacing: 1, margin: '10px 0 6px', paddingBottom: 4, borderBottom: '2px solid #f3f3f3' }}>Addresses</div>
                {ctos.business_addresses.map((addr, i) => <AddrRow key={i} addr={addr} isFirst={i === 0} />)}
              </div>
            )}
            {ctos.financials?.length > 0 && (
              <div style={{ marginTop: 12, borderTop: '1px solid #f3f3f3', paddingTop: 12 }}>
                {[
                  { label: 'Balance Sheet', fields: [['Total Assets','total_assets'],['Total Liabilities','total_liabilities'],['Net Worth','net_worth'],['Paid-Up Capital','paid_up_capital'],['Non-Current Assets','non_current_assets'],['Current Assets','current_assets'],['Current Liabilities','current_liabilities'],['Long Term Liabilities','long_term_liabilities'],['Working Capital','working_capital']] },
                  { label: 'Income Statement', fields: [['Revenue','revenue'],['Profit Before Tax','profit_before_tax'],['Profit After Tax','profit_after_tax']] },
                  { label: 'Ratios', fields: [['Current Ratio','current_ratio'],['Working Capital Days','working_capital_days'],['PBT Margin %','pbt_margin'],['PAT Margin %','pat_margin'],['ROA %','roa'],['Return on Capital','return_on_capital'],['Current Asset Turnover','current_asset_turnover'],['Working Capital Turnover','working_capital_turnover'],['Gearing Ratio','gearing_ratio'],['Debt to Asset %','debt_asset_ratio'],['Dividend Pay-out Ratio','dividend_payout_ratio']] },
                ].map(({ label, fields }) => (
                  <div key={label} style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#c8102e', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6, paddingBottom: 4, borderBottom: '2px solid #f3f3f3' }}>{label}</div>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                        <thead><tr style={{ background: '#f8f9fa' }}>
                          <th style={{ padding: '5px 8px', border: '1px solid #eee', textAlign: 'left', fontWeight: 600 }}>Metric</th>
                          {ctos.financials.map((f, i) => (
                            <th key={i} style={{ padding: '5px 8px', border: '1px solid #eee', textAlign: 'right', fontWeight: 600, color: f.is_epc ? '#888' : 'inherit', background: f.is_epc ? '#fafafa' : 'inherit' }}>
                              {f.year || `Y${i+1}`}
                              {f.is_epc && <span style={{ display: 'block', fontSize: 10, color: '#b06000', fontWeight: 500 }}>(EPC)</span>}
                            </th>
                          ))}
                        </tr></thead>
                        <tbody>
                          {fields.map(([name, key]) => (
                            <tr key={key}>
                              <td style={{ padding: '4px 8px', border: '1px solid #eee', color: '#555' }}>{name}</td>
                              {ctos.financials.map((f, i) => {
                                const v = f[key];
                                return <td key={i} style={{ padding: '4px 8px', border: '1px solid #eee', textAlign: 'right', color: v != null && v < 0 ? '#c5221f' : 'inherit' }}>{v != null ? Number(v).toLocaleString() : ' - '}</td>;
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* -- Historical Credit Requests -- */}
        {ctx.historical_underwriting?.length > 0 && (
          <div style={s.card}>
            <div style={s.cardTitle}>Historical Credit Requests ({ctx.historical_underwriting.length} records)</div>
            <div style={{ fontSize: 11, color: '#888', marginBottom: 8 }}>Click a row to expand full remarks and details</div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: '#f8f9fa' }}>
                    {['','Submission Date','Requested','Outcome','Approval Date'].map(h => (
                      <th key={h} style={{ padding: '5px 8px', border: '1px solid #eee', textAlign: 'left', fontWeight: 600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ctx.historical_underwriting.map((h, i) => {
                    const isOpen = expandedHistorical.has(i);
                    const submitted = h.submitted_at
                      ? (h.submitted_at.includes('T') ? h.submitted_at.split('T')[0].split('-').reverse().join('/') : h.submitted_at.split(' ')[0].split('/').reverse().join('/'))
                      : ' - ';

                    // Compact one-line outcome for the collapsed row, based on
                    // how far this record actually got � never claims a stage
                    // happened if it didn't.
                    let outcomeLabel = ' - ', outcomeColor = '#888';
                    if (h.decision_type === 'credit_approved') {
                      outcomeLabel = `Approved${h.approved_limit ? ' - RM ' + fmt(h.approved_limit) : ''}`;
                      outcomeColor = '#1e7e34';
                    } else if (h.decision_type === 'kyc_rejected') {
                      outcomeLabel = 'L2 Rejected';
                      outcomeColor = '#c5221f';
                    } else if (h.decision_type === 'kyc_approved_pending_credit') {
                      outcomeLabel = 'L2 Approved (pending limit)';
                      outcomeColor = '#b06000';
                    } else if (h.decision_type === 'kyc_l1_rejected') {
                      outcomeLabel = 'L1 Rejected';
                      outcomeColor = '#c5221f';
                    }

                    const hasL1 = h.kyc_status || h.kyc_recommendation || h.kyc_remarks || h.rejection_reason;
                    const hasL2 = h.kyc_l2_status || h.kyc_l2_approver || h.kyc_l2_rejection_reason || h.kyc_l2_remarks;
                    const hasLimit = h.approved_limit || h.approver_remarks || h.credit_approver;

                    return (
                      <React.Fragment key={i}>
                        <tr
                          style={{ background: i % 2 === 0 ? '#fff' : '#fafafa', cursor: 'pointer' }}
                          onClick={() => setExpandedHistorical(prev => {
                            const next = new Set(prev);
                            next.has(i) ? next.delete(i) : next.add(i);
                            return next;
                          })}
                        >
                                                    <td style={{ padding: '4px 8px', border: '1px solid #eee', color: '#888', width: 16 }}>{isOpen ? 'v' : '>'}</td>
                          <td style={{ padding: '4px 8px', border: '1px solid #eee', whiteSpace: 'nowrap' }}>{submitted}</td>
                          <td style={{ padding: '4px 8px', border: '1px solid #eee' }}>{h.requested_limit ? 'RM ' + fmt(h.requested_limit) : ' - '}</td>
                          <td style={{ padding: '4px 8px', border: '1px solid #eee', fontWeight: 600, color: outcomeColor }}>{outcomeLabel}</td>
                          <td style={{ padding: '4px 8px', border: '1px solid #eee', whiteSpace: 'nowrap' }}>{h.review_date || ' - '}</td>
                        </tr>
                        {isOpen && (
                          <tr style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                            <td colSpan={5} style={{ padding: '10px 16px', border: '1px solid #eee' }}>
                              {h.sales_remarks && (() => {
                                let hrm = {};
                                try { hrm = JSON.parse(h.sales_remarks); } catch(e) { hrm = { other_remarks: h.sales_remarks }; }
                                const hFields = [
                                  hrm.limitJustification && { label: 'Limit Justification', value: hrm.limitJustification },
                                  hrm.limit_justification && { label: 'Limit Justification', value: hrm.limit_justification },
                                  hrm.latePaymentResponse && { label: 'Late Payment Response', value: hrm.latePaymentResponse },
                                  hrm.late_payment_response && { label: 'Late Payment Response', value: hrm.late_payment_response },
                                  hrm.other_remarks && { label: 'Sales Remarks', value: hrm.other_remarks },
                                  (hrm.is_resubmission && hrm.resubmission_reason) && { label: 'Resubmission Reason', value: hrm.resubmission_reason },
                                ].filter(Boolean);
                                if (hFields.length === 0) return null;
                                return (
                                  <div style={{ marginBottom: 10 }}>
                                    {hFields.map(f => (
                                      <div key={f.label} style={{ marginBottom: 8, padding: '8px 12px', background: '#f8f9fa', borderRadius: 6 }}>
                                        <div style={{ fontSize: 12, fontWeight: 700, color: '#c8102e', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>{f.label}</div>
                                        <div style={{ fontSize: 13, color: '#555', fontStyle: 'italic' }}>"{f.value}"</div>
                                      </div>
                                    ))}
                                  </div>
                                );
                              })()}
                              {hasL1 && (
                                <div style={{ marginBottom: 10 }}>
                                  <div style={{ fontSize: 11, fontWeight: 700, color: '#555', marginBottom: 2 }}>
                                    L1 KYC � <span style={{ color: h.kyc_status === 'Pass' ? '#1e7e34' : h.kyc_status === 'Fail' ? '#c5221f' : '#b06000' }}>{h.kyc_status || h.kyc_recommendation || '-'}</span>
                                  </div>
                                  <div style={{ color: '#333' }}>{h.rejection_reason || h.kyc_remarks || ' - '}</div>
                                </div>
                              )}
                              {hasL2 && (
                                <div style={{ marginBottom: 10 }}>
                                  <div style={{ fontSize: 11, fontWeight: 700, color: '#555', marginBottom: 2 }}>
                                    L2 KYC � <span style={{ color: h.kyc_l2_status === 'Approve' ? '#1e7e34' : h.kyc_l2_status === 'Reject' ? '#c5221f' : '#b06000' }}>{h.kyc_l2_status || '-'}</span>
                                    {h.kyc_l2_approver && <span style={{ fontWeight: 400, color: '#888' }}> ({h.kyc_l2_approver})</span>}
                                  </div>
                                  <div style={{ color: '#333' }}>{h.kyc_l2_rejection_reason || h.kyc_l2_remarks || ' - '}</div>
                                </div>
                              )}
                              {hasLimit && (
                                <div>
                                  <div style={{ fontSize: 11, fontWeight: 700, color: '#555', marginBottom: 2 }}>
                                    LIMIT DECISION{h.approved_limit ? <span style={{ color: '#1e7e34' }}> � RM {fmt(h.approved_limit)}</span> : ''}
                                  </div>
                                  <div style={{ color: '#333' }}>{h.approver_remarks || ' - '}</div>
                                  {h.credit_approver && <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>Approved by {h.credit_approver}</div>}
                                </div>
                              )}
                              {!h.sales_remarks && !hasL1 && !hasL2 && !hasLimit && (
                                <div style={{ color: '#888' }}>No further details recorded for this submission.</div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* -- Repayment Metrics, Invoices, Online Search, AI, Market Intel, Strengths, Risks, Rationale - limit stage only -- */}
        {kyc2Approved && (<>
        {rm.invoice_count != null && (
          <div style={s.card}>
            <div style={s.cardTitle}>Repayment &amp; AR Metrics</div>
            <div style={s.grid2}>
              <KV label="Total Invoices" value={rm.invoice_count} />
              <KV label="Weighted DPD" value={rm.weighted_dpd != null ? Number(rm.weighted_dpd).toFixed(2) : null} />
              <KV label="Average DPD" value={rm.average_dpd != null ? Number(rm.average_dpd).toFixed(2) : null} />
              <KV label="Median DPD" value={rm.median_dpd != null ? Number(rm.median_dpd).toFixed(2) : null} />
              <KV label="Late Invoice %" value={rm.late_invoice_ratio != null ? (rm.late_invoice_ratio * 100).toFixed(1) + '%' : null} />
              <KV label="Trend" value={rm.trend_label ? rm.trend_label.replace(/_/g, ' ') : null} />
              <KV label="Current AR Balance" value={'RM ' + fmt(rm.current_outstanding || 0)} />
              {rm.dpd_excluded_count > 0 && <KV label="DPD-Excluded" value={rm.dpd_excluded_count + ' (operational/technical)'} />}
            </div>

            <div style={{ fontSize: 11, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.02em', margin: '12px 0 8px' }}>Cash Capacity Signals</div>

            {/* Largest 30-day settlement - peak, incl. late, decomposed */}
            {rm.largest_due_window_exposure_30d != null ? (
              <div style={{ background: '#fafafa', borderRadius: 6, padding: '8px 10px', marginBottom: 6 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <span style={{ fontSize: 12, color: '#666' }}>Largest 30-day settlement</span>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>RM {fmt(rm.largest_due_window_exposure_30d)}</span>
                </div>
                <div style={{ fontSize: 10.5, color: '#999', marginTop: 2 }}>
                  Highest amount settled within any 30-day period across its full history.
                  {rm.largest_due_window_exposure_30d_period ? ' Period from ' + fmtDateShort(rm.largest_due_window_exposure_30d_period) : ''}
                  {rm.largest_due_window_exposure_invoice_count_30d != null ? ' . ' + rm.largest_due_window_exposure_invoice_count_30d + ' invoice' + (rm.largest_due_window_exposure_invoice_count_30d === 1 ? '' : 's') : ''}
                  {rm.largest_due_window_late_portion_30d != null && rm.largest_due_window_late_portion_30d > 0
                    ? ' . RM ' + fmt(rm.largest_due_window_late_portion_30d) + ' (' + (rm.largest_due_window_late_portion_ratio_30d * 100).toFixed(0) + '%) was late'
                    : ''}
                </div>
              </div>
            ) : (
              <div style={{ background: '#fafafa', borderRadius: 6, padding: '8px 10px', marginBottom: 6 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <span style={{ fontSize: 12, color: '#666' }}>Largest 30-day settlement</span>
                  <span style={{ fontSize: 13, color: '#999' }}> - </span>
                </div>
              </div>
            )}

            {/* Largest 30-day prompt settlement - peak, clean only, zero late */}
            {rm.largest_due_window_settlement_30d != null ? (
              <div style={{ background: '#fafafa', borderRadius: 6, padding: '8px 10px', marginBottom: 6 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <span style={{ fontSize: 12, color: '#666' }}>Largest 30-day prompt settlement</span>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>RM {fmt(rm.largest_due_window_settlement_30d)}</span>
                </div>
                <div style={{ fontSize: 10.5, color: '#999', marginTop: 2 }}>
                  Highest amount settled <i>promptly</i> within any 30-day period across its full history.
                  {rm.largest_due_window_settlement_30d_period ? ' Period from ' + fmtDateShort(rm.largest_due_window_settlement_30d_period) : ''}
                  {rm.largest_due_window_invoice_count_30d != null ? ' . ' + rm.largest_due_window_invoice_count_30d + ' invoice' + (rm.largest_due_window_invoice_count_30d === 1 ? '' : 's') : ''}
                </div>
              </div>
            ) : (
              <div style={{ background: '#fafafa', borderRadius: 6, padding: '8px 10px', marginBottom: 6 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <span style={{ fontSize: 12, color: '#666' }}>Largest 30-day prompt settlement</span>
                  <span style={{ fontSize: 13, color: '#999' }}> - </span>
                </div>
                <div style={{ fontSize: 10.5, color: '#999', marginTop: 2 }}>No fully clean 30-day settlement in its history</div>
              </div>
            )}

            {/* Baseline capacity - median, all windows incl. late, full history */}
            <div style={{ background: '#fafafa', borderRadius: 6, padding: '8px 10px', marginBottom: 6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <span style={{ fontSize: 12, color: '#666' }}>Baseline Capacity</span>
                <span style={{ fontSize: 13, fontWeight: 600 }}>{rm.baseline_capacity_p50 != null ? 'RM ' + fmt(rm.baseline_capacity_p50) : ' - '}</span>
              </div>
              <div style={{ fontSize: 10.5, color: '#999', marginTop: 2 }}>
                Median (P50) 30-day settlement amount, across its full history
                {rm.baseline_capacity_window_count != null ? ' . ' + rm.baseline_capacity_window_count + ' window' + (rm.baseline_capacity_window_count === 1 ? '' : 's') : ''}
              </div>
            </div>

            {/* Recent capacity - average, clean only, last 180 days */}
            <div style={{ background: '#fafafa', borderRadius: 6, padding: '8px 10px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <span style={{ fontSize: 12, color: '#666' }}>Recent Capacity</span>
                <span style={{ fontSize: 13, fontWeight: 600 }}>{rm.recent_capacity_avg != null ? 'RM ' + fmt(rm.recent_capacity_avg) : ' - '}</span>
              </div>
              <div style={{ fontSize: 10.5, color: '#999', marginTop: 2 }}>
                Average 30-day settlement (prompt repayment only), based on recent repayment history
                {rm.recent_capacity_window_count != null ? ' . ' + rm.recent_capacity_window_count + ' window' + (rm.recent_capacity_window_count === 1 ? '' : 's') : ''}
              </div>
            </div>
          </div>
        )}

        {/* -- Invoice History -- */}
        {invoices.length > 0 && (
          <div style={s.card}>
            <div style={s.cardTitle}>
              Invoice History&nbsp;
              <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: '#aaa' }}>
                {rm.invoice_count && rm.invoice_count > invoices.length ? `(showing ${invoices.length} of ${rm.invoice_count})` : `(${invoices.length})`}
              </span>
              {data.assessmentCreatedAt && (
                <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: '#aaa', marginLeft: 8 }}>
                  (as at {fmtDateShort(data.assessmentCreatedAt)})
                </span>
              )}
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: '#f8f9fa' }}>
                    {['Invoice ID','Date','Due','Settlement','Amount','DPD','Outstanding','Payments','Type(s)','Status','DPD Flag'].map(h => (
                      <th key={h} style={{ padding: '5px 6px', border: '1px solid #eee', textAlign: h === 'Invoice ID' ? 'left' : 'right', fontWeight: 600, whiteSpace: 'nowrap', fontSize: 11 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv, i) => {
                    const excluded = Number(inv.dpd_inclusion_flag ?? 1) === 0;
                    const isPredue = inv.invoice_type === 'Predue';
                    const isCN = inv.invoice_type?.startsWith('CN') || inv.invoice_type?.startsWith('CP');
                    const rowStyle = excluded ? { background: '#f8f8f8', color: '#999' } : isPredue ? { background: '#f0f7ff' } : isCN ? { background: '#fff8e1' } : { background: i % 2 === 0 ? '#fff' : '#fafafa' };
                    const payTypes = (() => { if (!inv.payment_references) return ' - '; const refs = String(inv.payment_references).split(',').map(r=>r.trim()).filter(Boolean); const seen = []; refs.forEach(r => { const c = r.substring(0,2).toUpperCase(); if (!seen.includes(c)) seen.push(c); }); return seen.length ? seen.join(', ') : ' - '; })();
                    const payCount = inv.total_payments != null ? inv.total_payments : inv.payment_references ? String(inv.payment_references).split(',').filter(s=>s.trim()).length : ' - ';
                    return (
                      <tr key={i} style={rowStyle}>
                        <td style={{ padding: '4px 6px', border: '1px solid #eee' }}>{inv.invoice_id}</td>
                        <td style={{ padding: '4px 6px', border: '1px solid #eee', textAlign: 'right', whiteSpace: 'nowrap' }}>{inv.invoice_date?.split('T')[0] || ' - '}</td>
                        <td style={{ padding: '4px 6px', border: '1px solid #eee', textAlign: 'right', whiteSpace: 'nowrap' }}>{inv.due_date?.split('T')[0] || ' - '}</td>
                        <td style={{ padding: '4px 6px', border: '1px solid #eee', textAlign: 'right', whiteSpace: 'nowrap' }}>{inv.settlement_date?.split('T')[0] || ' - '}</td>
                        <td style={{ padding: '4px 6px', border: '1px solid #eee' }}>{fmt(inv.invoice_amount)}</td>
                        <td style={{ padding: '4px 6px', border: '1px solid #eee', textAlign: 'right', color: excluded ? '#999' : inv.aging_days > (om.visit_cycle_days || 14) ? '#c5221f' : '#1e7e34' }}>{isPredue ? ' - ' : (inv.aging_days ?? ' - ')}</td>
                        <td style={{ padding: '4px 6px', border: '1px solid #eee' }}>{inv.new_outstanding ?? 0}</td>
                        <td style={{ padding: '4px 6px', border: '1px solid #eee' }}>{payCount}</td>
                        <td style={{ padding: '4px 6px', border: '1px solid #eee' }}>{payTypes}</td>
                        <td style={{ padding: '4px 6px', border: '1px solid #eee' }}>{inv.invoice_type}</td>
                        <td style={{ padding: '4px 6px', border: '1px solid #eee', textAlign: 'center' }}>
                          {excluded ? <span style={{ display:'inline-block', background:'#f1f1f1', color:'#888', borderRadius:3, padding:'1px 5px', fontSize:10 }}>Excl.</span> : <span style={{ display:'inline-block', background:'#e6f4ea', color:'#1e7e34', borderRadius:3, padding:'1px 5px', fontSize:10 }}>Incl.</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div style={{ fontSize: 11, color: '#999', marginTop: 6 }}>
                <span style={{ display:'inline-block', background:'#f1f1f1', color:'#888', borderRadius:3, padding:'1px 5px', fontSize:10 }}>Excl.</span> = excluded from DPD metrics &nbsp;
                <span style={{ display:'inline-block', background:'#e6f4ea', color:'#1e7e34', borderRadius:3, padding:'1px 5px', fontSize:10 }}>Incl.</span> = included &nbsp;
                <span style={{ color:'#1967d2', fontWeight:600 }}>*</span> Predue &nbsp;
                <span style={{ color:'#b06000', fontWeight:600 }}>*</span> CN/CP
              </div>
            </div>
          </div>
        )}


        {/* -- Online Search -- */}
        <div style={s.card}>
          <div style={s.cardTitle}>Online Search</div>
          <div style={{ fontSize: 13, color: '#555', marginBottom: 10, fontStyle: 'italic' }}>{os.summary || 'No search data.'}</div>
          {(os.relevant_results || []).map((r, i) => (
            <div key={i} style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
              <div style={{ fontWeight: 600, color: '#1967d2', fontSize: 13 }}>{r.title}</div>
              <div style={{ fontSize: 12, color: '#555' }}>{r.snippet}</div>
              {r.link && <div style={{ fontSize: 11, color: '#999' }}><a href={r.link} target="_blank" rel="noopener noreferrer">{r.link}</a></div>}
            </div>
          ))}
        </div>

        {/* -- Automation Pending Notice -- */}
        {!data.hasAia && (
          <div style={{ background: '#fff3e0', border: '1px solid #ffe0b2', borderRadius: 8, padding: '12px 16px', marginBottom: 12, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <span style={{ fontSize: 18, flexShrink: 0 }}>[!]</span>
            <div>
              <div style={{ fontWeight: 700, color: '#e65100', marginBottom: 4 }}>Automation Pending</div>
              <div style={{ fontSize: 13, color: '#bf360c', lineHeight: 1.5 }}>
                The AI assessment pipeline has not run for this submission yet. You may review and make a manual decision using the basic submission details, or wait for automation to complete.
              </div>
            </div>
          </div>
        )}

        {/* -- AI Assessment -- */}
        {data.hasAia && (
        <div style={s.card}>
          <div style={s.cardTitle}>AI Assessment</div>
          <div style={s.pills}>
            <div style={{ ...s.pill, background: RISK_BG[data.aiRisk], color: RISK_CLR[data.aiRisk] }}>
              <div style={s.pillVal}>{data.aiRisk || ' - '}</div>
              <div style={s.pillLbl}>Overall Risk</div>
            </div>
            <div style={{ ...s.pill, background: GRADE_BG[data.aiGrade] || '#f1f1f1', color: GRADE_CLR[data.aiGrade] || '#5f6368' }}>
              <div style={s.pillVal}>{data.aiGrade || 'No Data'}</div>
              <div style={s.pillLbl}>Repayment Tier</div>
            </div>
            <div style={{ ...s.pill, background: CONF_BG[data.aiConfidence], color: CONF_CLR[data.aiConfidence] }}>
              <div style={s.pillVal}>{data.aiConfidence || ' - '}</div>
              <div style={s.pillLbl}>Confidence</div>
            </div>
          </div>
          <div style={s.reco}>
            <div>
              <div style={s.recoLbl}>AI Recommended Limit</div>
              {data.aiRecLimit ? (
                <>
                  <div style={s.recoAmt}>RM {fmt(data.aiRecLimit)}</div>
                  <div style={s.recoTerm}>{data.aiRecTerm} day terms</div>
                </>
              ) : (
                <>
                  <div style={{ ...s.recoAmt, color: '#ff6b6b' }}>DECLINE</div>
                  <div style={s.recoTerm}>
                    {om.current_limit > 0
                      ? `Current request declined - existing RM ${fmt(om.current_limit)} limit stays unchanged`
                      : 'Current request declined - no facility extended at this time'}
                  </div>
                </>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {ctx.underwriting_analytics?.is_new_lead && <span style={{ background: 'rgba(255,255,255,0.2)', borderRadius: 6, padding: '4px 10px', fontSize: 12 }}>New Lead</span>}
              {ctx.underwriting_analytics?.is_petrol_mart && <span style={{ background: 'rgba(255,255,255,0.2)', borderRadius: 6, padding: '4px 10px', fontSize: 12 }}>Petrol Mart</span>}
              {a.allianz_tci_recommended && <span style={{ background: 'rgba(255,200,0,0.3)', borderRadius: 6, padding: '4px 10px', fontSize: 12 }}>TCI Recommended</span>}
            </div>
          </div>
          <KV label="Limit Interpretation" value={a.limit_request_interpretation} />
          <KV label="Limit Support" value={a.requested_limit_support} />
        </div>
        )}

        {/* -- Market Intelligence -- */}
        {mi.market_summary && (
          <div style={s.card}>
            <div style={s.cardTitle}>
              Market Intelligence <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: '#aaa' }}>(AI . Gemini)</span>
              {mi.confidence && (
                <span style={{
                  marginLeft: 8,
                  fontSize: 10,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  padding: '2px 8px',
                  borderRadius: 10,
                  color: '#fff',
                  background: mi.confidence === 'high' ? '#1a7f37'
                    : mi.confidence === 'medium' ? '#b58105'
                    : mi.confidence === 'low' ? '#b54708'
                    : '#888',
                }}>
                  {mi.confidence} confidence
                </span>
              )}
            </div>
            {mi.status && mi.status !== 'success' && (
              <div style={{ padding: '8px 12px', background: '#fff4e5', border: '1px solid #ffca8a', borderRadius: 8, marginBottom: 10, fontSize: 12, color: '#8a5200' }}>
                [!] {mi.status === 'insufficient_data_fetch_failed'
                  ? 'Places lookup failed - this is a data gap, not confirmation of no nearby competitors. Manual review recommended.'
                  : mi.status === 'insufficient_data'
                  ? 'Insufficient evidence (no photo, no nearby POIs) - fields below are not reliable.'
                  : mi.status === 'no_geocode'
                  ? 'No geocode available - market analysis skipped.'
                  : mi.status === 'parse_error'
                  ? 'Market intelligence response failed to parse - manual review recommended.'
                  : 'Market intelligence assessment incomplete.'}
              </div>
            )}
            <div style={{ padding: '10px 12px', background: '#f8f9fa', borderRadius: 8, marginBottom: 12, fontSize: 13, color: '#444', lineHeight: 1.6 }}>
              {mi.market_summary}
            </div>
            <div style={s.grid2}>
              <KV label="Outlet Size" value={mi.outlet_size ? `${mi.outlet_size} (${mi.estimated_sqft_band} sqft)` : ' - '} />
              <KV label="Shop Condition" value={mi.shop_condition} />
              <KV label="Road Frontage" value={mi.road_frontage?.replace(/_/g, ' ')} />
              <KV label="Traffic Profile" value={mi.customer_traffic_profile?.replace(/_/g, ' ')} />
              <KV label="Est. Monthly Rev" value={mi.estimated_monthly_revenue ? `RM${mi.estimated_monthly_revenue.toLocaleString()} (${mi.estimated_monthly_revenue_band})` : mi.estimated_monthly_revenue_band} />
              <KV label="Competitors 500m" value={mi.competitor_count_500m != null ? `${mi.competitor_count_500m}${mi.nearest_competitor ? ' (nearest: ' + mi.nearest_competitor + ')' : ''}` : ' - '} />
            </div>
            {mi.estimated_monthly_revenue_rationale && (
              <div style={{ fontSize: 12, color: '#666', marginTop: 8, fontStyle: 'italic' }}>
                [TIP] {mi.estimated_monthly_revenue_rationale}
                {mi.revenue_estimate_basis && (
                  <span style={{ marginLeft: 6, fontStyle: 'normal', fontSize: 10, color: '#999', textTransform: 'uppercase' }}>
                    [{mi.revenue_estimate_basis === 'dosm_grounded' ? 'DOSM district data'
                      : mi.revenue_estimate_basis === 'self_listing_signal' ? 'review-corroborated guess'
                      : 'heuristic guess only'}]
                  </span>
                )}
                {mi.revenue_calculation_breakdown && (
                  <button
                    onClick={() => setShowRevenueBreakdown(v => !v)}
                    style={{ marginLeft: 8, fontStyle: 'normal', fontSize: 10, color: '#4a7dff', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
                  >
                    {showRevenueBreakdown ? 'hide calculation' : 'show calculation'}
                  </button>
                )}
              </div>
            )}
            {showRevenueBreakdown && mi.revenue_calculation_breakdown && (
              <div style={{ marginTop: 8, padding: '10px 12px', background: '#fafbfc', border: '1px solid #eee', borderRadius: 8, fontSize: 11.5, color: '#555' }}>
                <div style={{ fontWeight: 700, color: '#888', fontSize: 10, marginBottom: 6, letterSpacing: 0.3 }}>REVENUE CALCULATION BREAKDOWN</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'auto auto', rowGap: 4, columnGap: 12 }}>
                  <span>Base archetype midpoint</span>
                  <span style={{ textAlign: 'right', fontFamily: 'monospace' }}>
                    RM{mi.revenue_calculation_breakdown.base_midpoint?.toLocaleString()}
                    {mi.revenue_calculation_breakdown.is_petrol_channel ? ' (petrol channel)' : ''}
                  </span>
                  <span>x Income factor (DOSM)</span>
                  <span style={{ textAlign: 'right', fontFamily: 'monospace' }}>
                    {mi.revenue_calculation_breakdown.income_factor?.toFixed(3)}
                    {!mi.revenue_calculation_breakdown.dosm_used && <span style={{ color: '#bbb' }}> (no match, neutral)</span>}
                  </span>
                  <span>x Competition factor</span>
                  <span style={{ textAlign: 'right', fontFamily: 'monospace' }}>
                    {mi.revenue_calculation_breakdown.competition_factor?.toFixed(3)}
                    <span style={{ color: '#bbb' }}> ({mi.revenue_calculation_breakdown.competitor_count_used} competitor{mi.revenue_calculation_breakdown.competitor_count_used === 1 ? '' : 's'})</span>
                  </span>
                  <span>x Popularity factor</span>
                  <span style={{ textAlign: 'right', fontFamily: 'monospace' }}>
                    {mi.revenue_calculation_breakdown.popularity_factor?.toFixed(3)}
                    {!mi.revenue_calculation_breakdown.self_listing_used && <span style={{ color: '#bbb' }}> (no listing, neutral)</span>}
                  </span>
                  <span style={{ fontWeight: 700, borderTop: '1px solid #e5e5e5', paddingTop: 4 }}>= Final estimate</span>
                  <span style={{ fontWeight: 700, textAlign: 'right', fontFamily: 'monospace', borderTop: '1px solid #e5e5e5', paddingTop: 4 }}>
                    RM{mi.estimated_monthly_revenue?.toLocaleString()}/mo
                  </span>
                </div>
              </div>
            )}
            {mi.competitor_names?.length > 0 && (
              <div style={{ fontSize: 12, color: '#555', marginTop: 6 }}>
                <span style={{ fontWeight: 700, color: '#888', fontSize: 11 }}>NEARBY COMPETITORS: </span>
                {mi.competitor_names.join(', ')}
              </div>
            )}
            {(mi.inventory_assortment?.length > 0 || mi.secondary_services?.length > 0) && (
              <div style={{ marginTop: 10, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                {mi.inventory_assortment?.length > 0 && (
                  <div style={{ flex: 1, minWidth: 160 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#888', marginBottom: 4 }}>INVENTORY</div>
                    <div style={{ fontSize: 12, color: '#444' }}>{mi.inventory_assortment.join(', ')}</div>
                  </div>
                )}
                {mi.secondary_services?.length > 0 && (
                  <div style={{ flex: 1, minWidth: 160 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#888', marginBottom: 4 }}>SERVICES</div>
                    <div style={{ fontSize: 12, color: '#444' }}>{mi.secondary_services.join(', ')}</div>
                  </div>
                )}
              </div>
            )}
            {(mi.nm_product_fit_score || mi.nm_recommended_categories?.length > 0) && (
              <div style={{ marginTop: 12, padding: '10px 12px', background: '#f0f7ff', borderRadius: 8, borderLeft: '3px solid #1967d2' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#1967d2', marginBottom: 4 }}>
                  NM Fit: {mi.nm_product_fit_score?.toUpperCase()}
                </div>
                {mi.nm_product_fit_rationale && <div style={{ fontSize: 12, color: '#444', marginBottom: 6 }}>{mi.nm_product_fit_rationale}</div>}
                {mi.nm_recommended_brands?.length > 0 && (
                  <div style={{ fontSize: 12, color: '#555' }}>
                    <strong>Recommended brands:</strong> {mi.nm_recommended_brands.join(', ')}
                  </div>
                )}
                {mi.commercial_opportunity_notes && (
                  <div style={{ fontSize: 12, color: '#555', marginTop: 4, fontStyle: 'italic' }}>{mi.commercial_opportunity_notes}</div>
                )}
              </div>
            )}
          </div>
        )}

        {/* -- Strengths -- */}
        {a.strengths?.length > 0 && (
          <div style={s.card}>
            <div style={s.cardTitle}>Strengths</div>
            <ul style={{ ...s.list, color: '#1e7e34' }}>
              {a.strengths.map((x, i) => <li key={i}>{x}</li>)}
            </ul>
          </div>
        )}

        {/* -- Risks -- */}
        {a.risks?.length > 0 && (
          <div style={s.card}>
            <div style={s.cardTitle}>Risks</div>
            <ul style={{ ...s.list, color: '#c5221f' }}>
              {a.risks.map((x, i) => <li key={i}>{x}</li>)}
            </ul>
          </div>
        )}

        {/* -- Additional Info Required --
             Deepseek doesn't always return this as an array of bullet
             points - confirmed a real case (row 860) where it returned a
             single prose-summary string instead, which has its own
             .length (passing the array guard below) but no .map(),
             crashing the whole page. Handle both shapes explicitly rather
             than assume the model is always schema-conformant. */}
        {a.additional_information_required && (
          Array.isArray(a.additional_information_required)
            ? a.additional_information_required.length > 0 && (
                <div style={s.card}>
                  <div style={s.cardTitle}>Additional Information</div>
                  <ul style={{ ...s.list, color: '#1967d2' }}>
                    {a.additional_information_required.map((x, i) => <li key={i}>{x}</li>)}
                  </ul>
                </div>
              )
            : (
                <div style={s.card}>
                  <div style={s.cardTitle}>Additional Information</div>
                  <div style={{ fontSize: 13, color: '#1967d2' }}>{a.additional_information_required}</div>
                </div>
              )
        )}

        {/* -- Rationale -- */}
        {a.rationale && (
          <div style={s.card}>
            <div style={s.cardTitle}>Rationale</div>
            <div style={s.rationale}>{a.rationale}</div>
          </div>
        )}

        </>)} {/* end kyc2Approved gate */}

      </div>

      {/* -- L1 KYC Checklist (read-only) - shown to approver at L2 stage -- */}
      {!limitDecided && data.finalDecision !== 'Fail KYC' && (
        <div style={{ maxWidth: 860, margin: '0 auto 16px', padding: '0 20px' }}>
          {data.kycPreparedBy && data.kycPreparedBy.startsWith('System') && (
            <div style={{ background: '#fff4e5', border: '1px solid #ffb84d', borderRadius: 8, padding: '12px 16px', marginBottom: 12 }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: '#8a5200', marginBottom: 4 }}>
                [!] CTOS Not Extracted - Name Check Failed
              </div>
              <div style={{ fontSize: 13, color: '#6b3f00', lineHeight: 1.5 }}>
                CTOS extraction for this outlet was deliberately skipped because {data.kycChecks?.kyc_remarks || 'of a failed name check'}.
              </div>
            </div>
          )}
          <div style={{ ...s.card, border: '1px solid #e0e0e0', ...(data.kycStatus === 'Fail' || data.kycStatus === 'Reject' ? { borderLeft: '4px solid #c5221f' } : {}) }}>
            <div style={{ ...s.cardTitle, display: 'flex', alignItems: 'center', gap: 8 }}>
  L1 KYC Check
  {(data.kycStatus === 'Fail' || data.kycStatus === 'Reject') ? (
    <span style={{ background: '#c5221f', color: '#fff', borderRadius: 5, padding: '3px 10px', fontSize: 12, fontWeight: 800, letterSpacing: 0, textTransform: 'none' }}>
      {data.kycStatus}
    </span>
  ) : data.kycStatus === 'Pass' ? (
    <span style={{ color: '#1e7e34', fontWeight: 700, letterSpacing: 0, textTransform: 'none', fontSize: 13 }}>� Pass</span>
  ) : data.kycStatus === 'Escalate' ? (
    <span style={{ color: '#f0a500', fontWeight: 700, letterSpacing: 0, textTransform: 'none', fontSize: 13 }}>� Escalate</span>
  ) : (
    <span style={{ color: '#888', fontWeight: 600, letterSpacing: 0, textTransform: 'none', fontSize: 13 }}>� Pending</span>
  )}
</div>
            <div style={s.grid2}>
              {[
                ['BRN Check', data.kycChecks?.brn_check],
                ['Entity Status', data.kycChecks?.entity_status],
                ['Geocode Check', data.kycChecks?.geocode_check],
                ['Duplicate Check', data.kycChecks?.duplicate_check],
              ].map(([label, val]) => {
                const isPass = val === 'TRUE' || val === true;
                const isFail = val === 'FALSE' || val === false;
                return (
                  <div key={label} style={s.kv}>
                    <span style={s.kvLbl}>{label}</span>
                    <span style={{ ...s.kvVal, color: isPass ? '#1e7e34' : isFail ? '#c5221f' : '#888', fontWeight: 600 }}>
                      {isPass ? 'Pass' : isFail ? 'Fail' : ' - '}
                    </span>
                  </div>
                );
              })}
              <KV label="Duplicate Count" value={data.kycChecks?.duplicate_count || ' - '} />
              {data.kycChecks?.ctos_extracted !== '' && (
                <div style={s.kv}>
                  <span style={s.kvLbl}>CTOS Extracted</span>
                  <span style={{ ...s.kvVal, color: (['TRUE',1,'1',true].includes(data.kycChecks?.ctos_extracted)) ? '#1e7e34' : '#c5221f', fontWeight: 600 }}>
                    {(['TRUE',1,'1',true].includes(data.kycChecks?.ctos_extracted)) ? 'Yes' : 'No'}
                  </span>
                </div>
              )}
            </div>
            {data.kycChecks?.kyc_remarks && (
              <div style={{ marginTop: 10, padding: '8px 12px', background: '#f8f9fa', borderRadius: 6, fontSize: 13, color: '#555', fontStyle: 'italic' }}>
                Checker Comment: "{data.kycChecks.kyc_remarks}"
              </div>
            )}
            {(data.kycPreparedBy || data.kycPreparedDate) && (
              <div style={{ fontSize: 12, color: '#888', marginTop: 8 }}>
                {[data.kycPreparedBy, data.kycPreparedDate].filter(Boolean).join(' . ')}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Duplicate Check Results - read only for KYC prep/general viewers.
          KYC L2 reviewer (same person/condition eligible to Approve below)
          gets an inline "Confirm Related Party" action per unresolved row. */}
      {(() => {
        const dupCheck = ctx.duplicate_check;
        if (!dupCheck?.checked) return null;
        const amastMatches = dupCheck.amast_matches || [];
        const obligorMatches = dupCheck.obligor_matches || [];
        // Same eligibility as the Approve/Reject/KIV buttons below - kept as
        // one boolean so the two blocks can't silently drift out of sync.
        const canReviewKyc2 = !kyc2Done && user.role !== 'kyc' && data.kycStatus
          && !(data.kycPreparedBy && user.displayName === data.kycPreparedBy);
        return (
          <div style={{ maxWidth: 860, margin: '0 auto 16px', padding: '0 20px' }}>
            <div style={s.card}>
              <div style={s.cardTitle}>Potential Duplicates by Outlet Name</div>
              {amastMatches.length === 0 ? (
                <div style={{ fontSize: 13, color: '#1e7e34', padding: '8px 0' }}>No potential duplicates found in Amast.</div>
              ) : (
                <>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: '#f3f5f6' }}>
                        {['Outlet ID','Name','City','Status','Decision'].map(h => (
                          <th key={h} style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, color: '#555', borderBottom: '1px solid #e0e0e0' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {amastMatches.map(m => {
                        const isObligorConfirmed = m.relation_source === 'obligor_confirmed';
                        const needsL2Confirm = canReviewKyc2 && m.relation_type === 'related_party'
                          && !isObligorConfirmed && !m.relation_confirmed_by_l2;
                        const rowBg = m.relation_type === 'duplicate' ? '#fff3f3'
                          : m.relation_type === 'related_party' ? '#f2f0ff'
                          : m.relation_type === 'none' ? '#f3fff5'
                          : '#fff';
                        return (
                          <tr key={m.outlet_id} style={{ borderBottom: '1px solid #f0f0f0', background: rowBg }}>
                            <td style={{ padding: '6px 8px' }}>{m.outlet_id}</td>
                            <td style={{ padding: '6px 8px', fontWeight: 600 }}>{m.name}</td>
                            <td style={{ padding: '6px 8px' }}>{m.city}</td>
                            <td style={{ padding: '6px 8px' }}>{m.status}</td>
                            <td style={{ padding: '6px 8px' }}>
                              {isObligorConfirmed ? (
                                <span style={{ fontWeight: 700, color: '#5b3fd4', fontSize: 11 }}>
                                  [OK] Related Party (Obligor: {m.obligor_group_name})
                                </span>
                              ) : m.relation_confirmed_by_l2 ? (
                                <span style={{ fontWeight: 700, color: '#5b3fd4', fontSize: 11 }}>
                                  * Related Party - confirmed by {m.relation_confirmed_by} ({m.obligor_group_name})
                                </span>
                              ) : needsL2Confirm ? (
                                relationConfirmOpen === m.outlet_id ? (
                                  <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                    <input value={groupNameInput} onChange={e => setGroupNameInput(e.target.value)}
                                      placeholder="Group Name" style={{ fontSize: 11, padding: '3px 6px', border: '1px solid #ddd', borderRadius: 4, width: 110 }} />
                                    <button onClick={() => handleConfirmRelation(m.outlet_id)} disabled={relationConfirmSaving}
                                      style={{ padding: '3px 8px', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600, fontSize: 11, background: '#5b3fd4', color: '#fff' }}>
                                      {relationConfirmSaving ? '...' : 'Save'}
                                    </button>
                                    <button onClick={() => { setRelationConfirmOpen(null); setGroupNameInput(''); }}
                                      style={{ padding: '3px 8px', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600, fontSize: 11, background: '#eee', color: '#333' }}>
                                      Cancel
                                    </button>
                                  </div>
                                ) : (
                                  <button onClick={() => { setRelationConfirmOpen(m.outlet_id); setGroupNameInput(''); }}
                                    style={{ padding: '3px 8px', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600, fontSize: 11, background: '#5b3fd4', color: '#fff' }}>
                                    Confirm Related Party
                                  </button>
                                )
                              ) : (
                                <span style={{ fontWeight: 700, color: m.relation_type === 'duplicate' ? '#c5221f' : m.relation_type === 'related_party' ? '#5b3fd4' : '#888' }}>
                                  {m.relation_type === 'duplicate' ? 'Duplicate' : m.relation_type === 'related_party' ? '* Related Party' : m.relation_type === 'none' ? 'Not Related' : 'Pending review'}
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {dupCheck.manual_extra_count > 0 && (
                    <div style={{ marginTop: 8, fontSize: 12, color: '#c5221f', fontWeight: 600 }}>
                      + {dupCheck.manual_extra_count} additional duplicate(s) identified manually
                    </div>
                  )}
                </>
              )}
            </div>
            {obligorMatches.length > 0 && (
              <div style={s.card}>
                <div style={s.cardTitle}>Related Parties (Obligor)</div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: '#f3f5f6' }}>
                      {['Outlet ID','Name','Group'].map(h => (
                        <th key={h} style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, color: '#555', borderBottom: '1px solid #e0e0e0' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {obligorMatches.map(m => (
                      <tr key={m.outlet_id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '6px 8px' }}>{m.outlet_id}</td>
                        <td style={{ padding: '6px 8px', fontWeight: 600 }}>{m.name}</td>
                        <td style={{ padding: '6px 8px', color: '#888' }}>{m.group_name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })()}

      {/* -- L2 KYC Sign-off -- */}
      {!limitDecided && data.finalDecision !== 'Fail KYC' && (
        <div style={{ maxWidth: 860, margin: '0 auto 100px', padding: '0 20px' }}>
          <div style={{ ...s.card, border: kyc2Approved ? '1.5px solid #34a853' : kyc2.status === 'KIV' ? '1.5px solid #7b1fa2' : kyc2Done ? '1.5px solid #c5221f' : '1.5px solid #1967d2' }}>
            <div style={s.cardTitle}>
              KYC Decision
              {kyc2HasInfo && <span style={{ marginLeft: 8, textTransform: 'none', letterSpacing: 0, fontWeight: 700, color: kyc2Approved ? '#1e7e34' : kyc2.status === 'KIV' ? '#7b1fa2' : '#c5221f' }}>
                {kyc2Approved ? 'Approved' : kyc2.status === 'KIV' ? 'KYC L2 On Hold' : 'Rejected'} by {kyc2.approver} on {kyc2.date}
              </span>}
            </div>
            {kyc2HasInfo && (
              <div style={{ ...s.grid2, marginBottom: kyc2Done ? 0 : 12 }}>
                <KV label="Decision" value={kyc2.status} />
                <KV label="Approver" value={kyc2.approver} />
                <KV label="Date" value={kyc2.date} />
                <KV label="KYC Expiry" value={kyc2.expiry} />
                {kyc2.rejection_reason && <KV label="Rejection Reason" value={kyc2.rejection_reason} />}
                {kyc2.remarks && <KV label="Remarks" value={kyc2.remarks} />}
              </div>
            )}
            {/* KIV is not a final outcome: show the info above AND keep the decision buttons. */}
            {kyc2Done ? null : user.role === 'kyc' ? (
              <div style={{ fontSize: 13, color: '#888', padding: '8px 0' }}>
                L2 KYC approval is handled by the approving team.
              </div>
            ) : !data.kycStatus ? (
              <div style={{ fontSize: 13, color: '#b06000', padding: '8px 0' }}>
                [PENDING] Awaiting L1 KYC validation before L2 review can proceed.
              </div>
            ) : data.kycPreparedBy && user.displayName === data.kycPreparedBy ? (
              <div style={{ fontSize: 13, color: '#c5221f', padding: '8px 0' }}>
                [BLOCKED] You submitted the L1 KYC check - a different approver must do the L2 review.
              </div>
            ) : (
              (() => {
                const amastMatches = (ctx.duplicate_check?.amast_matches) || [];
                const unresolvedCount = amastMatches.filter(m =>
                  m.relation_type === 'related_party'
                  && m.relation_source !== 'obligor_confirmed'
                  && !m.relation_confirmed_by_l2
                ).length;
                const approveBlocked = unresolvedCount > 0 || (signboardMismatch && !signboardAck2);
                return (
                  <>
                    {unresolvedCount > 0 && (
                      <div style={{ fontSize: 12, color: '#c5221f', fontWeight: 600, marginBottom: 8 }}>
                        [BLOCKED] {unresolvedCount} Related Party flag(s) above still need confirmation before this can be approved.
                      </div>
                    )}
                    {signboardMismatch && (
                      <div style={{ background: '#fff8e1', border: '1px solid #f2c94c', borderRadius: 6, padding: '8px 10px', marginBottom: 8 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#8a6d00' }}>[!] Signboard name does not match registered name</div>
                        <div style={{ fontSize: 11.5, color: '#6b5900', marginTop: 2, lineHeight: 1.4 }}>
                          Signboard reads {signboardTexts.map(t => `"${t}"`).join(', ')}, but the registered/trading name is
                          {' '}"{om.outlet_name || ctos.entity_name}".
                        </div>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, fontSize: 12, color: '#333', cursor: 'pointer' }}>
                          <input type="checkbox" checked={signboardAck2} onChange={e => setSignboardAck2(e.target.checked)} />
                          I have reviewed this discrepancy
                        </label>
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button style={{ ...s.btn, background: '#34a853', color: '#fff', flex: 1, opacity: approveBlocked ? 0.5 : 1, cursor: approveBlocked ? 'not-allowed' : 'pointer' }}
                        disabled={approveBlocked}
                        onClick={() => { setKyc2Decision('Approve'); setKyc2Modal(true); }}>Approve</button>
                      <button style={{ ...s.btn, background: '#ea4335', color: '#fff', flex: 1 }} onClick={() => { setKyc2Decision('Reject'); setKyc2Modal(true); }}>Reject</button>
                      <button style={{ ...s.btn, background: '#7b1fa2', color: '#fff', flex: 1 }} onClick={() => { setKyc2Decision('KIV'); setKyc2Modal(true); }}>KIV</button>
                    </div>
                  </>
                );
              })()
            )}
          </div>
        </div>
      )}

      {/* -- Decision bar - only show when KYC approved and limit not yet decided -- */}
      {kyc2Approved && !decided && user.role !== 'kyc' && (() => {
        const maxLimit = user.maxLimit || 0;
        const needsEscalation = Math.max(data.aiRecLimit || 0, data.reqLimit || 0) > maxLimit;
        const isEscalated = !!data.l1Recommendation;
        const l1r = data.l1Recommendation;

        // "Decline" in this system means the existing limit stays unchanged - 
        // it is never a downsize (that's handled separately by monthly
        // review). Per the existing prompt convention, the AI outputs a null
        // recommended_limit for BOTH "no limit warranted" and "maintain
        // existing limit" - both cases are treated identically here.
        const aiRecommendsDecline = !data.aiRecLimit;
        // Escalation only ever happens for approve-type recommendations - L1 cannot
        // escalate a decline - so once escalated, it's never treated as a decline.
        const effectiveRecommendsDecline = isEscalated ? false : aiRecommendsDecline;

        return (
          <>
            {/* L1 Recommendation card - visible to L2+ when case is escalated */}
            {isEscalated && (
              <div style={{ maxWidth: 860, margin: '0 auto 16px', padding: '0 20px' }}>
                <div style={{ ...s.card, border: '1.5px solid #f0a500' }}>
                  <div style={s.cardTitle}>L1 Recommendation</div>
                  <div style={s.grid2}>
                    <KV label="Recommended by" value={l1r.recommender} />
                    <KV label="Date" value={l1r.date} />
                    <KV label="Decision" value={l1r.recommended_decision === 'as_recommended' ? (l1r.recommended_limit ? 'Approve as Recommended' : 'Decline as Recommended') : l1r.recommended_decision === 'variation' ? 'Approve with Variation' : 'Approve with TCI'} />
                    <KV label="Recommended Limit" value={l1r.recommended_limit ? 'RM ' + fmt(l1r.recommended_limit) : ' - '} />
                    <KV label="Recommended Term" value={l1r.recommended_term ? l1r.recommended_term + ' days' : ' - '} />
                  </div>
                  <div style={{ marginTop: 10 }}>
                    <span style={s.kvLbl}>Justification</span>
                    <div style={{ fontSize: 13, color: '#333', marginTop: 4 }}>{l1r.justification}</div>
                  </div>
                </div>
              </div>
            )}

            <div style={s.decBar}>
              {user.email === data.requestorEmail ? (
                <div style={{ fontSize: 13, color: '#c5221f', fontWeight: 600 }}>
                  [BLOCKED] You submitted this request - a different approver must make the credit decision.
                </div>
              ) : data.kycPreparedBy && user.displayName === data.kycPreparedBy ? (
                <div style={{ fontSize: 13, color: '#c5221f', fontWeight: 600 }}>
                  [BLOCKED] You performed the L1 KYC check - a different approver must make the credit decision.
                </div>
              ) : (
                <>
                  {overdueChecked ? (
                    <button style={{ ...s.btn, background: '#34a853', color: '#fff' }} onClick={() => setModal('approveChoice')}>Approve</button>
                  ) : (
                    <button style={{ ...s.btn, background: overdueBlocked ? '#c5221f' : '#7b1fa2', color: '#fff' }}
                      onClick={handleOverdueCheck} disabled={overdueCheckLoading}>
                      {overdueCheckLoading ? 'Checking...' : 'Check Current Overdue Status'}
                    </button>
                  )}
                  <button style={{ ...s.btn, background: '#ea4335', color: '#fff' }} onClick={() => setModal('declineChoice')}>Decline</button>
                  {!['Credit Query','Waiting for Sales Reply'].includes(effectiveDecision) && (
                    <button style={{ ...s.btn, background: '#5f6368', color: '#fff' }} onClick={() => setModal('query')}>Credit Query</button>
                  )}
                </>
              )}
            </div>
            {overduePopup && (
              <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 16px' }}>
                <div style={{ background: '#fff', borderRadius: 14, padding: '24px 20px', maxWidth: 480, width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
                  {overduePopup.type === 'clear' && (
                    <>
                      <div style={{ fontSize: 15, fontWeight: 700, color: '#1e7e34', marginBottom: 10 }}>No Current Overdue</div>
                      <div style={{ fontSize: 13, color: '#555', lineHeight: 1.6, marginBottom: 16 }}>
                        No current overdue balance for this outlet.
                      </div>
                    </>
                  )}
                  {overduePopup.type === 'upcoming' && (
                    <>
                      <div style={{ fontSize: 15, fontWeight: 700, color: '#b06000', marginBottom: 10 }}>Invoice Coming Due</div>
                      <div style={{ fontSize: 13, color: '#555', lineHeight: 1.6, marginBottom: 16 }}>
                        No current overdue, but the following invoice(s) are coming due soon:
                        {overduePopup.invoices.map((inv, i) => (
                          <div key={i} style={{ marginTop: 8 }}>
                            Invoice {inv.invoiceId}: RM {fmt(inv.amount)}, due in {inv.daysUntilDue} day{inv.daysUntilDue === 1 ? '' : 's'}
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                  {overduePopup.type === 'overdue' && (
                    <>
                      <div style={{ fontSize: 15, fontWeight: 700, color: '#c5221f', marginBottom: 10 }}>Overdue Balance Found</div>
                      <div style={{ fontSize: 13, color: '#555', lineHeight: 1.6, marginBottom: 16 }}>
                        {overduePopup.invoices.map((inv, i) => (
                          <div key={i} style={{ marginTop: 8 }}>
                            Invoice {inv.invoiceId}: RM {fmt(inv.amount)}, overdue by {inv.daysOverdue} day{inv.daysOverdue === 1 ? '' : 's'}
                          </div>
                        ))}
                        <div style={{ marginTop: 10, fontWeight: 600 }}>No approval permitted until overdue is settled.</div>
                      </div>
                    </>
                  )}
                  <div style={s.modalBtns}>
                    <button style={{ ...s.btn, background: '#eee', color: '#333' }} onClick={() => setOverduePopup(null)}>Close</button>
                  </div>
                </div>
              </div>
            )}
            {(data.finalDecision === 'Waiting for Sales Reply' || data.finalDecision === 'Credit Query') && (
              <div style={{ maxWidth: 860, margin: '0 auto 100px', padding: '0 20px' }}>
              <CreditQueryCard
                mcRow={mcRow}
                outletId={data.outletId}
                outletName={data.outletName}
                role="approver"
                senderName={user?.displayName || ''}
                senderEmail={user?.email || ''}
                recipientEmail={data.requestorEmail || ''}
                lang="en"
                onQuerySent={() => {}}
              />
              </div>
            )}
          </>
        );
      })()}

      {/* -- Toast -- */}
      {toast && (
        <div style={{ position: 'fixed', bottom: 80, left: '50%', transform: 'translateX(-50%)', background: '#1e7e34', color: '#fff', padding: '12px 24px', borderRadius: 8, fontWeight: 600, fontSize: 14, zIndex: 300 }}>
          {toast}
        </div>
      )}

      {/* -- Modals -- */}
      {/* -- L2 KYC Modal -- */}
      {kyc2Modal && (
        <Modal title={`${kyc2Decision === 'Approve' ? 'Approve KYC' : kyc2Decision === 'Reject' ? 'Reject KYC' : 'KIV'}`} onClose={() => { setKyc2Modal(false); }}>
          {kyc2Decision === 'Reject' && (
            <>
              <label style={s.label}>Rejection Reason *</label>
              <select style={{ ...s.input, background: '#fff' }} value={kyc2Reason} onChange={e => setKyc2Reason(e.target.value)}>
                <option value=""> - Select - </option>
                <option>Inaccurate SSM / Outlet number</option>
                <option>Expired Business License</option>
                <option>Duplicate Outlet Creation</option>
                <option>Discrepancy in Address / Coordinates</option>
                <option>Fail Site Visit Photo</option>
                <option>Others</option>
              </select>
            </>
          )}
          <label style={s.label}>{kyc2Decision === 'KIV' ? 'Remarks * (required - why is this being held?)' : 'Remarks (optional)'}</label>
          <textarea style={s.textarea} value={kyc2Remarks} onChange={e => setKyc2Remarks(e.target.value)} placeholder="Any remarks..." />
          <div style={s.modalBtns}>
            <button style={{ ...s.btn, flex: 2, background: kyc2Decision === 'Approve' ? '#1e7e34' : kyc2Decision === 'Reject' ? '#c5221f' : '#7b1fa2', color: '#fff' }}
              onClick={handleKyc2Submit} disabled={kyc2Saving || !kyc2Decision || (kyc2Decision === 'KIV' && !kyc2Remarks.trim())}>
              {kyc2Saving ? 'Saving...' : 'Confirm'}
            </button>
            <button style={{ ...s.btn, flex: 1, background: '#eee', color: '#333' }} onClick={() => setKyc2Modal(false)}>Cancel</button>
          </div>
        </Modal>
      )}

      {modal === 'approveChoice' && (() => {
        const isEsc = !!data.l1Recommendation;
        const aiDecline = !data.aiRecLimit;
        const effDecline = isEsc ? false : aiDecline;
        return (
        <Modal title="Approve" onClose={() => setModal(null)}>
          <div style={s.modalBtns}>
            <button style={{ ...s.btn, flex: 1, background: effDecline ? '#ccc' : '#34a853', color: '#fff', cursor: effDecline ? 'not-allowed' : 'pointer' }}
              disabled={effDecline}
              onClick={() => setModal('approve')}>Approved As Recommended</button>
            <button style={{ ...s.btn, flex: 1, background: '#f0a500', color: '#fff' }} onClick={() => setModal('variation')}>Approve with Variation</button>
            <button style={{ ...s.btn, flex: 1, background: '#c8102e', color: '#fff' }} onClick={() => setModal('tci')}>Approved with TCI</button>
          </div>
          <div style={{ ...s.modalBtns, marginTop: 12 }}>
            <button style={{ ...s.btn, flex: 1, background: '#eee', color: '#333' }} onClick={() => setModal(null)}>Cancel</button>
          </div>
        </Modal>
        );
      })()}

      {modal === 'declineChoice' && (() => {
        const isEsc = !!data.l1Recommendation;
        const aiDecline = !data.aiRecLimit;
        const effDecline = isEsc ? false : aiDecline;
        return (
        <Modal title="Decline" onClose={() => setModal(null)}>
          <div style={s.modalBtns}>
            <button style={{ ...s.btn, flex: 1, background: '#ea4335', color: '#fff' }} onClick={() => setModal('decline')}>
              {effDecline ? 'Reject as Recommended' : 'Reject Current Request'}
            </button>
            <button style={{ ...s.btn, flex: 1, background: '#c5221f', color: '#fff' }} onClick={() => setModal('revoke')}>Revoke Limit</button>
          </div>
          <div style={{ ...s.modalBtns, marginTop: 12 }}>
            <button style={{ ...s.btn, flex: 1, background: '#eee', color: '#333' }} onClick={() => setModal(null)}>Cancel</button>
          </div>
        </Modal>
        );
      })()}

      {modal === 'approve' && (() => {
        const l1r = data.l1Recommendation;
        const effLimit = l1r ? Number(l1r.recommended_limit || 0) : Number(data.aiRecLimit || 0);
        const effTerm  = l1r ? Number(l1r.recommended_term || 0)  : Number(data.aiRecTerm || 0);
        const overAuthority = effLimit > (user.maxLimit || Infinity);
        if (escalate) {
          return (
            <Modal title="Escalate for Approval" onClose={() => setEscalate(null)}>
              {escalate.stage === 'ask' ? (
                <>
                  <div style={{ fontSize: 13, color: '#333', marginBottom: 16 }}>
                    This exceeds your delegated authority. Escalate to a higher authority approver?
                  </div>
                  <div style={s.modalBtns}>
                    <button style={{ ...s.btn, flex: 1, background: '#eee', color: '#333' }} onClick={() => setEscalate(null)}>No</button>
                    <button style={{ ...s.btn, flex: 1, background: '#7b1fa2', color: '#fff' }}
                      onClick={() => setEscalate({ ...escalate, stage: 'confirm', target: resolveEscalationTarget(escalate.limit) })}>
                      Yes, escalate
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 13, color: '#333', marginBottom: 16 }}>
                    {escalate.target.ceiling
                      ? <>This exceeds standing delegated authority at every level. This will be sent to <strong>{escalate.target.name}</strong> for review, but a credit committee meeting should be initiated before sign-off given the amount involved.</>
                      : <>This will be sent to <strong>{escalate.target.name}</strong> for review. Please ensure your justification is clear.</>
                    }
                  </div>
                  <div style={s.modalBtns}>
                    <button style={{ ...s.btn, flex: 1, background: '#eee', color: '#333' }} onClick={() => setEscalate(null)}>Cancel</button>
                    <button style={{ ...s.btn, flex: 1, background: '#7b1fa2', color: '#fff' }} onClick={handleEscalate} disabled={saving}>
                      {saving ? 'Sending...' : 'Confirm & Send'}
                    </button>
                  </div>
                </>
              )}
            </Modal>
          );
        }
        return (
        <Modal title="Approve as Recommended" onClose={() => setModal(null)}>
          <div style={{ fontSize: 13, color: '#555', marginBottom: 12 }}>
            {l1r
              ? <>Approving L1 recommendation: <strong>RM {fmt(l1r.recommended_limit)}</strong> @ <strong>{l1r.recommended_term} days</strong></>
              : <>Approving RM {fmt(data.aiRecLimit)} @ {data.aiRecTerm} days</>
            }
          </div>
          <label style={s.label}>Remarks (optional)</label>
          <textarea style={s.textarea} value={justification} onChange={e => setJustification(e.target.value)} placeholder="Add any remarks..." />
          <div style={s.modalBtns}>
            <button style={{ ...s.btn, flex: 2, background: '#34a853', color: '#fff' }}
              onClick={() => overAuthority
                ? setEscalate({ stage: 'ask', type: l1r ? l1r.recommended_decision : 'as_recommended', limit: effLimit, term: effTerm, justification })
                : handleAction('approve_as_requested')}
              disabled={saving}>
              {saving ? 'Saving...' : 'Confirm Approval'}
            </button>
            <button style={{ ...s.btn, flex: 1, background: '#eee', color: '#333' }} onClick={() => setModal(null)}>Cancel</button>
          </div>
        </Modal>
        );
      })()}

      {modal === 'variation' && (() => {
        const effectiveLimit = customLimit ? Number(customLimit) : Number(data.aiRecLimit || 0);
        const overAuthority = effectiveLimit > (user.maxLimit || Infinity);
        const belowCurrent = effectiveLimit <= Number(om.current_limit || 0);
        if (escalate) {
          return (
            <Modal title="Escalate for Approval" onClose={() => setEscalate(null)}>
              {escalate.stage === 'ask' ? (
                <>
                  <div style={{ fontSize: 13, color: '#333', marginBottom: 16 }}>
                    This exceeds your delegated authority. Escalate to a higher authority approver?
                  </div>
                  <div style={s.modalBtns}>
                    <button style={{ ...s.btn, flex: 1, background: '#eee', color: '#333' }} onClick={() => setEscalate(null)}>No</button>
                    <button style={{ ...s.btn, flex: 1, background: '#7b1fa2', color: '#fff' }}
                      onClick={() => setEscalate({ ...escalate, stage: 'confirm', target: resolveEscalationTarget(escalate.limit) })}>
                      Yes, escalate
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 13, color: '#333', marginBottom: 16 }}>
                    {escalate.target.ceiling
                      ? <>This exceeds standing delegated authority at every level. This will be sent to <strong>{escalate.target.name}</strong> for review, but a credit committee meeting should be initiated before sign-off given the amount involved.</>
                      : <>This will be sent to <strong>{escalate.target.name}</strong> for review. Please ensure your justification is clear.</>
                    }
                  </div>
                  <div style={s.modalBtns}>
                    <button style={{ ...s.btn, flex: 1, background: '#eee', color: '#333' }} onClick={() => setEscalate(null)}>Cancel</button>
                    <button style={{ ...s.btn, flex: 1, background: '#7b1fa2', color: '#fff' }} onClick={handleEscalate} disabled={saving}>
                      {saving ? 'Sending...' : 'Confirm & Send'}
                    </button>
                  </div>
                </>
              )}
            </Modal>
          );
        }
        return (
        <Modal title="Approve with Variation" onClose={() => setModal(null)}>
          <label style={s.label}>Approved Limit (RM) - blank = AI recommended ({fmt(data.aiRecLimit)})</label>
          <input style={s.input} type="number" value={customLimit} onChange={e => setCustomLimit(e.target.value)} placeholder={data.aiRecLimit} />
          {overAuthority && (
            <div style={{ fontSize: 12, color: '#c5221f', marginTop: 4, fontWeight: 600 }}>
              RM {effectiveLimit.toLocaleString()} exceeds your delegated authority.
            </div>
          )}
          {!overAuthority && belowCurrent && (
            <div style={{ fontSize: 12, color: '#c5221f', marginTop: 4, fontWeight: 600 }}>
              [BLOCKED] Approved limit must be greater than the current limit (RM {fmt(om.current_limit)}).
            </div>
          )}
          <label style={s.label}>Approved Term (days) - blank = AI recommended ({data.aiRecTerm})</label>
          <input style={s.input} type="number" value={customTerm} onChange={e => setCustomTerm(e.target.value)} placeholder={data.aiRecTerm} />
          <label style={s.label}>Justification *</label>
          <textarea style={s.textarea} value={justification} onChange={e => setJustification(e.target.value)} placeholder="Reason for variation..." />
          <div style={s.modalBtns}>
            <button style={{ ...s.btn, flex: 2, background: '#f0a500', color: '#fff' }}
              onClick={() => overAuthority
                ? setEscalate({ stage: 'ask', type: 'variation', limit: effectiveLimit, term: Number(customTerm) || Number(data.aiRecTerm || 0), justification })
                : handleAction('approve_variation')}
              disabled={saving || belowCurrent}>
              {saving ? 'Saving...' : 'Confirm'}
            </button>
            <button style={{ ...s.btn, flex: 1, background: '#eee', color: '#333' }} onClick={() => setModal(null)}>Cancel</button>
          </div>
        </Modal>
        );
      })()}

      {modal === 'tci' && (() => {
        const effectiveLimit = customLimit ? Number(customLimit) : Number(data.reqLimit || 0);
        const overAuthority = effectiveLimit > (user.maxLimit || Infinity);
        const belowCurrent = effectiveLimit <= Number(om.current_limit || 0);
        if (escalate) {
          return (
            <Modal title="Escalate for Approval" onClose={() => setEscalate(null)}>
              {escalate.stage === 'ask' ? (
                <>
                  <div style={{ fontSize: 13, color: '#333', marginBottom: 16 }}>
                    This exceeds your delegated authority. Escalate to a higher authority approver?
                  </div>
                  <div style={s.modalBtns}>
                    <button style={{ ...s.btn, flex: 1, background: '#eee', color: '#333' }} onClick={() => setEscalate(null)}>No</button>
                    <button style={{ ...s.btn, flex: 1, background: '#7b1fa2', color: '#fff' }}
                      onClick={() => setEscalate({ ...escalate, stage: 'confirm', target: resolveEscalationTarget(escalate.limit) })}>
                      Yes, escalate
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 13, color: '#333', marginBottom: 16 }}>
                    {escalate.target.ceiling
                      ? <>This exceeds standing delegated authority at every level. This will be sent to <strong>{escalate.target.name}</strong> for review, but a credit committee meeting should be initiated before sign-off given the amount involved.</>
                      : <>This will be sent to <strong>{escalate.target.name}</strong> for review. Please ensure your justification is clear.</>
                    }
                  </div>
                  <div style={s.modalBtns}>
                    <button style={{ ...s.btn, flex: 1, background: '#eee', color: '#333' }} onClick={() => setEscalate(null)}>Cancel</button>
                    <button style={{ ...s.btn, flex: 1, background: '#7b1fa2', color: '#fff' }} onClick={handleEscalate} disabled={saving}>
                      {saving ? 'Sending...' : 'Confirm & Send'}
                    </button>
                  </div>
                </>
              )}
            </Modal>
          );
        }
        if (tciConfirm) {
          return (
            <Modal title="Confirm TCI Endorsement" onClose={() => setTciConfirm(false)}>
              <div style={{ fontSize: 13, color: '#333', marginBottom: 16 }}>
                Please check the Allianz TCI dashboard to ensure the limit is endorsed by the vendor before approving.
              </div>
              <div style={s.modalBtns}>
                <button style={{ ...s.btn, flex: 1, background: '#eee', color: '#333' }} onClick={() => setTciConfirm(false)}>Cancel</button>
                <button style={{ ...s.btn, flex: 1, background: '#c8102e', color: '#fff' }} onClick={() => { setTciConfirm(false); handleAction('approve_tci'); }} disabled={saving}>
                  {saving ? 'Saving...' : 'Confirmed - Approve'}
                </button>
              </div>
            </Modal>
          );
        }
        return (
        <Modal title="Approve with TCI" onClose={() => setModal(null)}>
          <label style={s.label}>Approved Limit (RM) - blank = requested ({fmt(data.reqLimit)})</label>
          <input style={s.input} type="number" value={customLimit} onChange={e => setCustomLimit(e.target.value)} placeholder={data.reqLimit} />
          {overAuthority && (
            <div style={{ fontSize: 12, color: '#c5221f', marginTop: 4, fontWeight: 600 }}>
              RM {effectiveLimit.toLocaleString()} exceeds your delegated authority.
            </div>
          )}
          {!overAuthority && belowCurrent && (
            <div style={{ fontSize: 12, color: '#c5221f', marginTop: 4, fontWeight: 600 }}>
              [BLOCKED] Approved limit must be greater than the current limit (RM {fmt(om.current_limit)}).
            </div>
          )}
          <label style={s.label}>Approved Term (days) - blank = AI recommended ({data.aiRecTerm})</label>
          <input style={s.input} type="number" value={customTerm} onChange={e => setCustomTerm(e.target.value)} placeholder={data.aiRecTerm} />
          <label style={s.label}>Justification *</label>
          <textarea style={s.textarea} value={justification} onChange={e => setJustification(e.target.value)} placeholder="Reason for TCI..." />
          <div style={s.modalBtns}>
            <button style={{ ...s.btn, flex: 2, background: '#c8102e', color: '#fff' }}
              onClick={() => overAuthority
                ? setEscalate({ stage: 'ask', type: 'tci', limit: effectiveLimit, term: Number(customTerm) || Number(data.aiRecTerm || 0), justification })
                : setTciConfirm(true)}
              disabled={saving || !justification.trim() || belowCurrent}>
              {saving ? 'Saving...' : 'Confirm TCI'}
            </button>
            <button style={{ ...s.btn, flex: 1, background: '#eee', color: '#333' }} onClick={() => setModal(null)}>Cancel</button>
          </div>
        </Modal>
        );
      })()}

      {modal === 'decline' && (() => {
        const isEsc = !!data.l1Recommendation;
        const aiDecline = !data.aiRecLimit;
        const effDecline = isEsc ? false : aiDecline;
        const label = effDecline ? 'Reject as Recommended' : 'Reject Current Request';
        return (
        <Modal title={label} onClose={() => setModal(null)}>
          <label style={s.label}>Decline Reason *</label>
          <textarea style={s.textarea} value={justification} onChange={e => setJustification(e.target.value)} placeholder="State reason for decline..." />
          <div style={s.modalBtns}>
            <button style={{ ...s.btn, flex: 2, background: '#ea4335', color: '#fff' }} onClick={() => handleAction('decline')} disabled={saving || !justification.trim()}>
              {saving ? 'Saving...' : `Confirm - ${label}`}
            </button>
            <button style={{ ...s.btn, flex: 1, background: '#eee', color: '#333' }} onClick={() => setModal(null)}>Cancel</button>
          </div>
        </Modal>
        );
      })()}

      {modal === 'revoke' && (
        <Modal title="Revoke Limit" onClose={() => setModal(null)}>
          <div style={{ fontSize: 13, color: '#c5221f', marginBottom: 12, fontWeight: 600 }}>
            This sends the outlet's limit and credit term to 0. Strong justification is required.
          </div>
          <label style={s.label}>Justification *</label>
          <textarea style={s.textarea} value={justification} onChange={e => setJustification(e.target.value)} placeholder="Reason for revoking limit..." />
          <div style={s.modalBtns}>
            <button style={{ ...s.btn, flex: 2, background: '#c5221f', color: '#fff' }}
              onClick={() => handleAction('revoke_limit')}
              disabled={saving || !justification.trim()}>
              {saving ? 'Saving...' : 'Confirm Revoke'}
            </button>
            <button style={{ ...s.btn, flex: 1, background: '#eee', color: '#333' }} onClick={() => setModal(null)}>Cancel</button>
          </div>
        </Modal>
      )}

      {modal === 'query' && (
        <Modal title="Credit Query" onClose={() => setModal(null)}>
          <label style={s.label}>Query *</label>
          <textarea style={s.textarea} value={queryText} onChange={e => setQueryText(e.target.value)} placeholder="What additional information is needed?" />
          <div style={s.modalBtns}>
            <button style={{ ...s.btn, flex: 2, background: '#5f6368', color: '#fff' }} onClick={async () => {
              if (!queryText.trim()) return;
              setSaving(true);
              try {
                const res = await fetch(`/api/credit-query/${mcRow}`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    role: 'approver',
                    senderName: user.displayName,
                    senderEmail: user.email,
                    message: queryText,
                    recipientEmail: data.requestorEmail || '',
                    outletId: data.outletId,
                  }),
                });
                const result = await res.json();
                if (result.success) {
                  if (result.notify) sendNotificationEmail(result.notify);
                  showToast('[OK] Credit query sent', false);
                  setModal(null);
                  setTimeout(() => navigate('/'), 1500);
                } else {
                  showToast(result.error || 'Unknown error', true);
                }
              } catch (e) {
                showToast(e.message, true);
              }
              setSaving(false);
            }} disabled={saving}>
              {saving ? 'Saving...' : 'Submit Query'}
            </button>
            <button style={{ ...s.btn, flex: 1, background: '#eee', color: '#333' }} onClick={() => setModal(null)}>Cancel</button>
          </div>
        </Modal>
      )}

    </div>
  );
}